import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import ResponsiveContainer from '@/components/ResponsiveContainer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tooltip as UITooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '../utils/toast';
import { BarChart, Bar, PieChart, Pie, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell, ResponsiveContainer as RechartsResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import axios from 'axios';
import { Award, TrendingUp, TrendingDown, Users, Activity, Star, Trophy, BarChart3, MessageSquare, Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const COLORS = ['#0056B3', '#00AEEF', '#4CAF50', '#FF9800', '#F44336'];
const COMPLIANCE_COLORS = ['#4CAF50', '#FF9800', '#F44336'];

// Tab Loading Indicator Component
const TabLoadingIndicator = ({ message = "The data is loading..." }) => {
  return (
    <div className="flex flex-col items-center justify-center py-16">
      <div className="relative">
        <div className="w-12 h-12 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
      </div>
      <p className="mt-4 text-gray-600 font-medium">{message}</p>
      <p className="text-sm text-gray-400 mt-1">Please wait while we fetch your reports...</p>
    </div>
  );
};

// Placeholder Widget Component
const PlaceholderWidget = ({ title, message, height = "250px", className = "" }) => {
  return (
    <div 
      className={`flex flex-col items-center justify-center bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg ${className}`}
      style={{ minHeight: height }}
    >
      <div className="text-center px-6 py-8">
        <div className="mb-3">
          <svg className="w-16 h-16 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        </div>
        {title && <h4 className="text-sm font-semibold text-gray-700 mb-2">{title}</h4>}
        <p className="text-sm text-gray-500">
          {message || "No data available."}
        </p>
        <p className="text-xs text-gray-400 mt-2">
          This report will appear once activities or records are generated.
        </p>
      </div>
    </div>
  );
};

const ReportsDashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  
  // Active tab state
  const [activeTab, setActiveTab] = useState('overview');
  
  // LAZY LOADING: Track which tabs are currently loading and which have been loaded (cached)
  const [tabLoading, setTabLoading] = useState({});
  const [loadedTabs, setLoadedTabs] = useState({});
  
  // Data for all reports
  const [overviewData, setOverviewData] = useState(null);
  const [auditData, setAuditData] = useState(null);
  const [patientCareData, setPatientCareData] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [costEffectivenessData, setCostEffectivenessData] = useState(null);
  const [feedbackData, setFeedbackData] = useState(null);
  const [rewardsData, setRewardsData] = useState(null);
  const [activityData, setActivityData] = useState(null);
  
  // Filters
  const [dateRange, setDateRange] = useState('last_30_days');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [matrixModalOpen, setMatrixModalOpen] = useState(false);
  const [matrixData, setMatrixData] = useState({ staff: [], topics: [], assignments: {} });
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [matrixFilterDepartment, setMatrixFilterDepartment] = useState('all');
  const [matrixFilterJobRole, setMatrixFilterJobRole] = useState('all');
  const [matrixFilterStaffName, setMatrixFilterStaffName] = useState('');
  const [matrixDepartments, setMatrixDepartments] = useState([]);
  const [matrixJobRoles, setMatrixJobRoles] = useState([]);
  const [staffDetailsModalOpen, setStaffDetailsModalOpen] = useState(false);
  const [staffDetailsData, setStaffDetailsData] = useState([]);
  const [staffDetailsFilter, setStaffDetailsFilter] = useState('all');
  const [staffDetailsLoading, setStaffDetailsLoading] = useState(false);
  const [departmentListModalOpen, setDepartmentListModalOpen] = useState(false);
  const [departmentListData, setDepartmentListData] = useState([]);
  const [departmentListLoading, setDepartmentListLoading] = useState(false);
  const [allPerformersModalOpen, setAllPerformersModalOpen] = useState(false);
  const [allPerformersData, setAllPerformersData] = useState([]);
  const [allPerformersLoading, setAllPerformersLoading] = useState(false);
  const [leadingDepartmentsModalOpen, setLeadingDepartmentsModalOpen] = useState(false);
  const [leadingDepartmentsData, setLeadingDepartmentsData] = useState([]);
  const [complianceModalOpen, setComplianceModalOpen] = useState(false);
  const [complianceModalType, setComplianceModalType] = useState(''); // 'fully', 'partially', 'non'
  const [complianceModalData, setComplianceModalData] = useState([]);
  const [leadingDepartmentsLoading, setLeadingDepartmentsLoading] = useState(false);
  const [topicFeedbackModalOpen, setTopicFeedbackModalOpen] = useState(false);
  const [topicFeedbackData, setTopicFeedbackData] = useState({ topic_name: '', feedback_list: [] });
  const [topicFeedbackLoading, setTopicFeedbackLoading] = useState(false);
  const [showAllDepartments, setShowAllDepartments] = useState(false);
  const [riskyDepartmentsModalOpen, setRiskyDepartmentsModalOpen] = useState(false);
  const [completionTrendData, setCompletionTrendData] = useState([]);
  const [hospitalSignupDate, setHospitalSignupDate] = useState(null); // Hospital signup date for default range
  const [trendDateRange, setTrendDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [monthlyStaffTrendData, setMonthlyStaffTrendData] = useState([]);
  const [monthlyTrendDateRange, setMonthlyTrendDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [monthlyTrendLoading, setMonthlyTrendLoading] = useState(false);
  const [activityByDepartment, setActivityByDepartment] = useState([]);
  const [activityByTopic, setActivityByTopic] = useState([]);
  const [activityDataLoading, setActivityDataLoading] = useState(false);
  
  // Topic Completion Band Distribution state
  const [topicDistributionData, setTopicDistributionData] = useState(null);
  const [topicDistributionLoading, setTopicDistributionLoading] = useState(false);
  const [topicDistributionModalOpen, setTopicDistributionModalOpen] = useState(false);
  const [selectedBandData, setSelectedBandData] = useState(null);
  const [hoveredBandIndex, setHoveredBandIndex] = useState(null);
  
  // Staff Performance Distribution (Composite Score) state
  const [staffPerfDistData, setStaffPerfDistData] = useState(null);
  const [staffPerfDistLoading, setStaffPerfDistLoading] = useState(false);
  const [staffPerfDistModalOpen, setStaffPerfDistModalOpen] = useState(false);
  const [selectedPerfBandData, setSelectedPerfBandData] = useState(null);
  const [hoveredPerfBandIndex, setHoveredPerfBandIndex] = useState(null);
  
  // Hospital Areas of Strength/Improvement state
  const [areasStrengthImprovementData, setAreasStrengthImprovementData] = useState(null);
  const [areasStrengthImprovementLoading, setAreasStrengthImprovementLoading] = useState(false);
  
  // Patient Care Impact Trend state
  const [patientCareImpactData, setPatientCareImpactData] = useState(null);
  const [patientCareImpactLoading, setPatientCareImpactLoading] = useState(false);
  const [patientCareImpactPeriod, setPatientCareImpactPeriod] = useState('monthly');
  
  // Department Outcomes state
  const [departmentOutcomesData, setDepartmentOutcomesData] = useState(null);
  const [departmentOutcomesLoading, setDepartmentOutcomesLoading] = useState(false);
  
  // Clinical Outcomes Guide expansion state
  const [clinicalGuideExpanded, setClinicalGuideExpanded] = useState(false);

  
  // Report tab configuration
  const reportTabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'audit', label: 'Audit & Compliance', icon: Activity },
    { id: 'patient', label: 'Clinical Outcomes', icon: Users },
    { id: 'activity', label: 'Staff Activity', icon: Activity },
    { id: 'performance', label: 'Staff Performance', icon: TrendingUp },
    // { id: 'cost', label: 'Cost-Effectiveness', icon: TrendingUp },
    { id: 'feedback', label: 'Feedback', icon: Star },
    { id: 'rewards', label: 'Rewards & Recognition', icon: Trophy },
  ];
  
  // ==================== LAZY LOADING TAB DATA FUNCTIONS ====================
  
  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };
  
  const baseURL = `${process.env.REACT_APP_BACKEND_URL}/api/hospital/reports`;
  
  // Load ONLY Overview tab data (called on initial page load)
  const loadOverviewData = async () => {
    try {
      const headers = getAuthHeaders();
      const overviewRes = await axios.get(`${baseURL}/overview`, { headers });
      if (overviewRes?.data) {
        const data = overviewRes.data;
        const topDepts = (data.top_departments || []).map(dept => ({
          name: dept.department_name,
          score: dept.avg_score,
          employee_count: dept.employee_count,
          total_assessments: dept.total_assessments
        }));
        
        const bottomDepts = (data.bottom_departments || []).map(dept => ({
          name: dept.department_name,
          score: dept.avg_score,
          employee_count: dept.employee_count,
          total_assessments: dept.total_assessments
        }));
        
        setOverviewData({
          staff_metrics: data.staff_metrics || { total_staff: 0, active_staff: 0, inactive_staff: 0, archived_staff: 0 },
          top_departments: topDepts,
          bottom_departments: bottomDepts,
          aggregate_feedback_score: data.average_feedback_score || 0,
          feedback_respondents: { 
            submitted: data.total_feedback_submissions || 0, 
            total: data.staff_metrics?.total_staff || 0 
          },
          top_performers: data.top_performers || [],
          departments_list: ['All Departments']
        });
      }
      setLoadedTabs(prev => ({ ...prev, overview: true }));
    } catch (err) {
      console.error('Error loading overview:', err);
    }
  };
  
  // Load Completion Trend data (part of Overview)
  const loadCompletionTrendData = async (startDate, endDate) => {
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${baseURL}/completion-trend`, { 
        headers,
        params: { start_date: startDate, end_date: endDate }
      });
      if (res?.data?.trend_data) {
        // Filter to only show days with completions (completions_count > 0)
        const daysWithActivity = res.data.trend_data.filter(day => day.completions_count > 0);
        setCompletionTrendData(daysWithActivity);
      }
    } catch (err) {
      console.error('Error loading completion trend:', err);
    }
  };
  
  // Load Audit & Compliance tab data
  const loadAuditData = async () => {
    if (loadedTabs.audit) return; // Already loaded (cached)
    setTabLoading(prev => ({ ...prev, audit: true }));
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${baseURL}/audit-compliance`, { headers });
      if (res?.data) setAuditData(res.data);
      setLoadedTabs(prev => ({ ...prev, audit: true }));
    } catch (err) {
      console.error('Error loading audit data:', err);
    } finally {
      setTabLoading(prev => ({ ...prev, audit: false }));
    }
  };
  
  // Load Patient Care tab data
  const loadPatientCareData = async () => {
    if (loadedTabs.patient) return;
    setTabLoading(prev => ({ ...prev, patient: true }));
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${baseURL}/patient-care`, { headers });
      if (res?.data) setPatientCareData(res.data);
      setLoadedTabs(prev => ({ ...prev, patient: true }));
      
      // Also load Patient Care Impact Trend data - await to ensure it completes
      await loadPatientCareImpactTrend('monthly');
      
      // Also load Department Outcomes data
      await loadDepartmentOutcomesData();
    } catch (err) {
      console.error('Error loading patient care data:', err);
      // Set empty data structure on error
      setPatientCareData({
        impact_trend: [],
        department_outcomes: [],
        staff_ratings: [],
        department_avg_ratings: []
      });
    } finally {
      setTabLoading(prev => ({ ...prev, patient: false }));
    }
  };
  
  // Load Patient Care Impact Trend data (from Evaluation Form)
  const loadPatientCareImpactTrend = async (period = 'monthly') => {
    try {
      setPatientCareImpactLoading(true);
      const headers = getAuthHeaders();
      const res = await axios.get(`${baseURL}/patient-care-impact-trend?period=${period}`, { headers });
      if (res?.data) {
        setPatientCareImpactData(res.data);
      }
    } catch (err) {
      console.error('Error loading patient care impact trend:', err);
    } finally {
      setPatientCareImpactLoading(false);
    }
  };
  
  // Handle Patient Care Impact period change
  const handlePatientCareImpactPeriodChange = (newPeriod) => {
    setPatientCareImpactPeriod(newPeriod);
    loadPatientCareImpactTrend(newPeriod);
  };
  
  // Load Department Outcomes data (from Evaluation Form)
  const loadDepartmentOutcomesData = async () => {
    try {
      setDepartmentOutcomesLoading(true);
      const headers = getAuthHeaders();
      const res = await axios.get(`${baseURL}/department-outcomes`, { headers });
      if (res?.data) setDepartmentOutcomesData(res.data);
    } catch (err) {
      console.error('Error loading department outcomes:', err);
    } finally {
      setDepartmentOutcomesLoading(false);
    }
  };
  
  // Load Staff Performance tab data
  const loadPerformanceData = async () => {
    if (loadedTabs.performance) return;
    setTabLoading(prev => ({ ...prev, performance: true }));
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${baseURL}/staff-performance`, { headers });
      if (res?.data) setPerformanceData(res.data);
      setLoadedTabs(prev => ({ ...prev, performance: true }));
      
      // Also load Staff Performance Distribution (Composite Score)
      loadStaffPerfDistData();
      
      // Also load Hospital Areas of Strength/Improvement
      loadAreasStrengthImprovementData();
    } catch (err) {
      console.error('Error loading performance data:', err);
      setPerformanceData({
        top_departments: [],
        strength_analysis: [],
        weakness_analysis: [],
        knowledge_distribution: []
      });
    } finally {
      setTabLoading(prev => ({ ...prev, performance: false }));
    }
  };
  
  // Load Staff Performance Distribution (Composite Score) data
  const loadStaffPerfDistData = async () => {
    try {
      setStaffPerfDistLoading(true);
      const headers = getAuthHeaders();
      const res = await axios.get(`${baseURL}/staff-performance-distribution`, { headers });
      if (res?.data) setStaffPerfDistData(res.data);
    } catch (err) {
      console.error('Error loading staff performance distribution:', err);
    } finally {
      setStaffPerfDistLoading(false);
    }
  };
  
  // Load Hospital Areas of Strength/Improvement data
  const loadAreasStrengthImprovementData = async () => {
    try {
      setAreasStrengthImprovementLoading(true);
      const headers = getAuthHeaders();
      const res = await axios.get(`${baseURL}/areas-strength-improvement`, { headers });
      if (res?.data) setAreasStrengthImprovementData(res.data);
    } catch (err) {
      console.error('Error loading areas strength/improvement:', err);
    } finally {
      setAreasStrengthImprovementLoading(false);
    }
  };
  
  // Handle performance band bar click
  const handlePerfBandBarClick = (bandData) => {
    setHoveredPerfBandIndex(null); // Hide tooltip when modal opens
    setSelectedPerfBandData(bandData);
    setStaffPerfDistModalOpen(true);
  };
  
  // Load Cost-Effectiveness tab data
  const loadCostData = async () => {
    if (loadedTabs.cost) return;
    setTabLoading(prev => ({ ...prev, cost: true }));
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${baseURL}/cost-effectiveness`, { headers });
      if (res?.data) setCostEffectivenessData(res.data);
      setLoadedTabs(prev => ({ ...prev, cost: true }));
    } catch (err) {
      console.error('Error loading cost data:', err);
      setCostEffectivenessData({
        pre_training: { training_effectiveness: 0, operational_efficiency: 0, compliance_readiness: 0, staff_satisfaction: 0 },
        post_training: { training_effectiveness: 0, operational_efficiency: 0, compliance_readiness: 0, staff_satisfaction: 0 },
        department_roi: []
      });
    } finally {
      setTabLoading(prev => ({ ...prev, cost: false }));
    }
  };
  
  // Load Feedback tab data
  const loadFeedbackData = async () => {
    if (loadedTabs.feedback) return;
    setTabLoading(prev => ({ ...prev, feedback: true }));
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${baseURL}/feedback`, { headers });
      if (res?.data) setFeedbackData(res.data);
      setLoadedTabs(prev => ({ ...prev, feedback: true }));
    } catch (err) {
      console.error('Error loading feedback data:', err);
    } finally {
      setTabLoading(prev => ({ ...prev, feedback: false }));
    }
  };
  
  // Load Rewards & Recognition tab data
  const loadRewardsData = async () => {
    if (loadedTabs.rewards) return;
    setTabLoading(prev => ({ ...prev, rewards: true }));
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${baseURL}/rewards`, { headers });
      if (res?.data) setRewardsData(res.data);
      setLoadedTabs(prev => ({ ...prev, rewards: true }));
    } catch (err) {
      console.error('Error loading rewards data:', err);
      setRewardsData({
        top_performers: [],
        rising_stars: [],
        department_leaders: []
      });
    } finally {
      setTabLoading(prev => ({ ...prev, rewards: false }));
    }
  };
  
  // Load Activity tab data
  const loadActivityTabData = async () => {
    if (loadedTabs.activity) return;
    setTabLoading(prev => ({ ...prev, activity: true }));
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${baseURL}/activity`, { headers });
      if (res?.data) setActivityData(res.data);
      setLoadedTabs(prev => ({ ...prev, activity: true }));
    } catch (err) {
      console.error('Error loading activity data:', err);
      setActivityData({
        active_staff_trend: [],
        department_activity: [],
        active_vs_inactive: [],
        topic_completion: [],
        attendance_completion: []
      });
    } finally {
      setTabLoading(prev => ({ ...prev, activity: false }));
    }
  };
  
  // Handle tab change - load data on demand
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    
    // Load tab data on demand (lazy loading)
    switch (tabId) {
      case 'overview':
        if (!loadedTabs.overview) loadOverviewData();
        break;
      case 'audit':
        loadAuditData();
        break;
      case 'patient':
        loadPatientCareData();
        break;
      case 'performance':
        loadPerformanceData();
        break;
      case 'cost':
        loadCostData();
        break;
      case 'feedback':
        loadFeedbackData();
        break;
      case 'rewards':
        loadRewardsData();
        break;
      case 'activity':
        loadActivityTabData();
        // Also load activity-specific data
        if (monthlyStaffTrendData.length === 0) {
          handleMonthlyTrendDateChange(monthlyTrendDateRange.startDate, monthlyTrendDateRange.endDate);
        }
        if (activityByDepartment.length === 0 || activityByTopic.length === 0) {
          loadActivityData();
        }
        // Load Completion Trend data (moved from Overview)
        if (completionTrendData.length === 0) {
          loadCompletionTrendData(trendDateRange.startDate, trendDateRange.endDate);
        }
        break;
      default:
        break;
    }
  };
  
  // Initial data load - ONLY Overview tab
  const loadRealData = async () => {
    try {
      // Load ONLY Overview tab data first
      await loadOverviewData();
      setLoading(false);
      
      // Note: Completion Trend is loaded by fetchSignupDateAndInit with correct dates
      
    } catch (error) {
      console.error('Error fetching reports data:', error);
      toast.error('Failed to load dashboard data');
      setLoading(false);
    }
  };
  
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) {
      navigate('/signin');
      return;
    }
    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    if (parsedUser.role !== 'Hospital Admin') {
      toast.error('Only Hospital Admins can access reports');
      navigate('/dashboard');
      return;
    }
    
    // Fetch hospital signup date for default date range
    const fetchSignupDateAndInit = async () => {
      let signupDate;
      const today = new Date().toISOString().split('T')[0];
      
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/hospital/signup-date`, { headers });
        signupDate = response.data.signup_date;
        setHospitalSignupDate(signupDate); // Store for Reset button
        setTrendDateRange({ startDate: signupDate, endDate: today });
        console.log('Initialized date range from signup date:', { startDate: signupDate, endDate: today });
      } catch (err) {
        // Fallback: Use 1 year ago if signup date fetch fails
        const oneYearAgo = new Date();
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        signupDate = oneYearAgo.toISOString().split('T')[0];
        setHospitalSignupDate(signupDate); // Store fallback for Reset button
        setTrendDateRange({ startDate: signupDate, endDate: today });
        console.log('Fallback date range (1 year):', { startDate: signupDate, endDate: today });
      }
      
      // Load completion trend with the correct dates immediately
      loadCompletionTrendData(signupDate, today);
    };
    
    fetchSignupDateAndInit();
    loadRealData();
  }, [navigate]);

  // Activity tab data loading is now handled in handleTabChange() for lazy loading

  const loadActivityData = async () => {
    try {
      setActivityDataLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const baseURL = `${process.env.REACT_APP_BACKEND_URL}/api/hospital/reports`;
      
      const [deptResponse, topicResponse] = await Promise.all([
        axios.get(`${baseURL}/activity-by-department`, { headers }),
        axios.get(`${baseURL}/activity-by-topic`, { headers })
      ]);
      
      if (deptResponse?.data?.departments) {
        setActivityByDepartment(deptResponse.data.departments);
      }
      if (topicResponse?.data?.topics) {
        setActivityByTopic(topicResponse.data.topics);
      }
      
      // Also load topic completion distribution
      loadTopicDistributionData();
    } catch (error) {
      console.error('Error loading activity data:', error);
    } finally {
      setActivityDataLoading(false);
    }
  };
  
  // Load Topic Completion Band Distribution data
  const loadTopicDistributionData = async () => {
    try {
      setTopicDistributionLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const baseURL = `${process.env.REACT_APP_BACKEND_URL}/api/hospital/reports`;
      
      const response = await axios.get(`${baseURL}/topic-completion-distribution`, { headers });
      
      if (response?.data) {
        setTopicDistributionData(response.data);
      }
    } catch (error) {
      console.error('Error loading topic distribution data:', error);
    } finally {
      setTopicDistributionLoading(false);
    }
  };
  
  // Handle band bar click - opens modal with all topics in that band
  const handleBandBarClick = (bandData) => {
    setHoveredBandIndex(null); // Hide tooltip when modal opens
    setSelectedBandData(bandData);
    setTopicDistributionModalOpen(true);
  };
  
  const handleStaffBoxClick = async (filterType) => {
    // Show modal with staff details instead of navigating
    setStaffDetailsFilter(filterType);
    setStaffDetailsModalOpen(true);
    await fetchStaffDetails(filterType);
  };
  
  const fetchStaffDetails = async (filterType) => {
    try {
      setStaffDetailsLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch staff details with completion data
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/hospital/staff-details?filter=${filterType}`,
        { headers }
      );
      
      setStaffDetailsData(response.data || []);
    } catch (error) {
      console.error('Error fetching staff details:', error);
      toast.error('Failed to load staff details');
      setStaffDetailsData([]);
    } finally {
      setStaffDetailsLoading(false);
    }
  };
  
  const handleDepartmentListClick = async (sortOrder = 'desc') => {
    setDepartmentListModalOpen(true);
    await fetchDepartmentList(sortOrder);
  };
  
  const fetchDepartmentList = async (sortOrder = 'desc') => {
    try {
      setDepartmentListLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch full department list with assessment score data
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/hospital/departments-completion`,
        { headers }
      );
      
      // Sort departments based on context
      // 'desc' = highest to lowest (for top departments)
      // 'asc' = lowest to highest (for bottom departments)
      const sortedData = (response.data || []).sort((a, b) => {
        if (sortOrder === 'asc') {
          return a.average_score - b.average_score;
        } else {
          return b.average_score - a.average_score;
        }
      });
      
      setDepartmentListData(sortedData);
    } catch (error) {
      console.error('Error fetching department list:', error);
      toast.error('Failed to load department list');
      setDepartmentListData([]);
    } finally {
      setDepartmentListLoading(false);
    }
  };

  const handleTrendDateChange = async (startDate, endDate) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const baseURL = `${process.env.REACT_APP_BACKEND_URL}/api/hospital/reports`;
      
      const response = await axios.get(`${baseURL}/completion-trend`, {
        headers,
        params: { start_date: startDate, end_date: endDate }
      });
      
      if (response?.data?.trend_data) {
        // Filter to show only days with actual activity
        const daysWithActivity = response.data.trend_data.filter(day => day.total_activities > 0);
        setCompletionTrendData(daysWithActivity);
        setTrendDateRange({ startDate, endDate });
      }
    } catch (error) {
      console.error('Error fetching completion trend:', error);
      toast.error('Failed to load completion trend data');
    }
  };

  const handleMonthlyTrendDateChange = async (startDate, endDate) => {
    try {
      setMonthlyTrendLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const baseURL = `${process.env.REACT_APP_BACKEND_URL}/api/hospital/reports`;
      
      const response = await axios.get(`${baseURL}/monthly-staff-trend`, {
        headers,
        params: { start_date: startDate, end_date: endDate }
      });
      
      if (response?.data?.monthly_data) {
        setMonthlyStaffTrendData(response.data.monthly_data);
        setMonthlyTrendDateRange({ startDate, endDate });
      }
    } catch (error) {
      console.error('Error fetching monthly staff trend:', error);
      toast.error('Failed to load monthly trend data');
    } finally {
      setMonthlyTrendLoading(false);
    }
  };
  
  const handleAllPerformersClick = async () => {
    setAllPerformersModalOpen(true);
    await fetchAllPerformers();
  };
  
  const fetchAllPerformers = async () => {
    try {
      setAllPerformersLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch all performers
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/hospital/all-performers`,
        { headers }
      );
      
      setAllPerformersData(response.data || []);
    } catch (error) {
      console.error('Error fetching all performers:', error);
      toast.error('Failed to load all performers');
      setAllPerformersData([]);
    } finally {
      setAllPerformersLoading(false);
    }
  };
  
  const handleLeadingDepartmentsClick = async () => {
    setLeadingDepartmentsModalOpen(true);
    await fetchLeadingDepartments();
  };
  
  const fetchLeadingDepartments = async () => {
    try {
      setLeadingDepartmentsLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch all departments by average score
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/hospital/leading-departments`,
        { headers }
      );
      
      setLeadingDepartmentsData(response.data || []);
    } catch (error) {
      console.error('Error fetching leading departments:', error);
      toast.error('Failed to load leading departments');
      setLeadingDepartmentsData([]);
    } finally {
      setLeadingDepartmentsLoading(false);
    }
  };
  
  const fetchMatrixData = async () => {
    setMatrixLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch staff, topics, and progress data in parallel
      const [staffRes, topicsRes, progressRes] = await Promise.allSettled([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/hospital-config/employees`, { headers }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/training/content`, { headers }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/hospital/topic-progress`, { headers })
      ]);
      
      const staff = staffRes.status === 'fulfilled' ? (staffRes.value?.data?.employees || []) : [];
      const topics = topicsRes.status === 'fulfilled' ? (topicsRes.value?.data || []) : [];
      const progress = progressRes.status === 'fulfilled' ? (progressRes.value?.data || []) : [];
      
      // Build assignments map: { [staffId]: { [topicId]: { completion, status } } }
      const assignments = {};
      
      // Filter staff with valid user_id
      const validStaff = staff.filter(s => s.user_id);
      
      validStaff.forEach(s => {
        assignments[s.user_id] = {};
      });
      
      progress.forEach(p => {
        if (p.user_id && p.content_id) {
          if (!assignments[p.user_id]) {
            assignments[p.user_id] = {};
          }
          assignments[p.user_id][p.content_id] = {
            completion: p.progress_percentage || 0,
            status: p.status || 'not_started'
          };
        }
      });
      
      // Update topics to use id as content_id for consistency
      const formattedTopics = topics.map(topic => ({
        ...topic,
        content_id: topic.id
      }));
      
      setMatrixData({ staff: validStaff, topics: formattedTopics, assignments });
      
      // Extract unique departments and job roles for filters
      // API returns department_name and job_function_name fields
      const depts = [...new Set(validStaff.map(s => s.department_name || s.department).filter(Boolean))].sort();
      const roles = [...new Set(validStaff.map(s => s.job_function_name || s.job_function || s.job_role).filter(Boolean))].sort();
      setMatrixDepartments(depts);
      setMatrixJobRoles(roles);
    } catch (error) {
      console.error('Error fetching matrix data:', error);
      toast.error('Failed to load matrix data');
    } finally {
      setMatrixLoading(false);
    }
  };
  
  useEffect(() => {
    if (matrixModalOpen) {
      // Reset filters when modal opens
      setMatrixFilterDepartment('all');
      setMatrixFilterJobRole('all');
      setMatrixFilterStaffName('');
      fetchMatrixData();
    }
  }, [matrixModalOpen]);

  // Update job roles dropdown when department filter changes
  useEffect(() => {
    if (matrixData.staff.length > 0) {
      if (matrixFilterDepartment === 'all') {
        // Show all job roles when no department is selected
        const allRoles = [...new Set(matrixData.staff.map(s => s.job_function_name || s.job_function || s.job_role).filter(Boolean))].sort();
        setMatrixJobRoles(allRoles);
      } else {
        // Filter job roles by selected department
        const deptStaff = matrixData.staff.filter(s => (s.department_name || s.department) === matrixFilterDepartment);
        const deptRoles = [...new Set(deptStaff.map(s => s.job_function_name || s.job_function || s.job_role).filter(Boolean))].sort();
        setMatrixJobRoles(deptRoles);
      }
      // Reset job role filter when department changes
      setMatrixFilterJobRole('all');
    }
  }, [matrixFilterDepartment, matrixData.staff]);

  
  const handleTopicFeedbackClick = async (topicId, topicName) => {
    setTopicFeedbackModalOpen(true);
    setTopicFeedbackLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/hospital/reports/feedback/topic/${topicId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setTopicFeedbackData(response.data);
    } catch (error) {
      console.error('Error fetching chapter feedback:', error);
      toast.error('Failed to load chapter feedback details');
    } finally {
      setTopicFeedbackLoading(false);
    }
  };

  
  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const halfStar = rating % 1 >= 0.5;
    return (
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`w-4 h-4 ${
              i < fullStars
                ? 'fill-yellow-400 text-yellow-400'
                : i === fullStars && halfStar
                ? 'fill-yellow-200 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
        <span className="ml-1 text-sm font-medium">{rating.toFixed(1)}</span>
      </div>
    );
  };
  
  const getBadgeColor = (badge) => {
    switch (badge) {
      case 'Gold': return 'bg-yellow-400 text-yellow-900';
      case 'Silver': return 'bg-gray-300 text-gray-900';
      case 'Bronze': return 'bg-orange-400 text-orange-900';
      default: return 'bg-blue-100 text-blue-900';
    }
  };
  
  if (loading || !user) {
    return (
      <AdminLayout>
        <ResponsiveContainer>
          <div className="flex items-center justify-center h-full min-h-screen">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Loading reports...</p>
            </div>
          </div>
        </ResponsiveContainer>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <ResponsiveContainer>
        <div className="mb-4 md:mb-6">
          <h1 className="text-xl md:text-2xl lg:text-2xl font-bold text-gray-900 mb-3 md:mb-4">Dashboard</h1>
          
          {/* Tab Navigation - Responsive */}
          <div className="flex gap-1 md:gap-2 border-b border-gray-200 overflow-x-auto">
            {reportTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`px-2 py-2 md:px-4 md:py-3 font-medium text-xs md:text-sm transition-all flex items-center gap-1 md:gap-2 border-b-2 whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600 bg-blue-50'
                      : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <Icon className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>
        </div>
        
        <div className="space-y-8">
          {/* A. Overview Page */}
          {activeTab === 'overview' && overviewData && (
            <Card className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-[24px] font-bold" style={{ color: '#0056B3' }}>Overview</h2>
                <button
                  onClick={() => setMatrixModalOpen(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  data-tooltip="View Staff Activity Matrix"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Staff Activity Matrix</span>
                </button>
              </div>
              
              {/* Staff Metrics Cards - Clickable */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <Card 
                  className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleStaffBoxClick('all')}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm text-gray-600">Total Staff Added</p>
                    <TooltipProvider>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <button className="text-gray-400 hover:text-gray-600" onClick={(e) => e.stopPropagation()}>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs bg-gray-900 text-white">
                          <p>Total staff registered under your institution.</p>
                        </TooltipContent>
                      </UITooltip>
                    </TooltipProvider>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-3xl font-bold" style={{ color: '#0056B3' }}>
                        {overviewData.staff_metrics.total_staff}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Click to view all</p>
                    </div>
                    <Users className="w-12 h-12 text-blue-400" />
                  </div>
                </Card>
                
                <Card 
                  className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleStaffBoxClick('active')}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm text-gray-600">Active Staff</p>
                    <TooltipProvider>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <button className="text-gray-400 hover:text-gray-600" onClick={(e) => e.stopPropagation()}>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs bg-gray-900 text-white">
                          <p>Staff who have started at least one learning activity.</p>
                        </TooltipContent>
                      </UITooltip>
                    </TooltipProvider>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-3xl font-bold text-green-600">
                        {overviewData.staff_metrics.active_staff}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Click to view active</p>
                    </div>
                    <Activity className="w-12 h-12 text-green-400" />
                  </div>
                </Card>
                
                <Card 
                  className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleStaffBoxClick('inactive')}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm text-gray-600">Inactive Staff</p>
                    <TooltipProvider>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <button className="text-gray-400 hover:text-gray-600" onClick={(e) => e.stopPropagation()}>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs bg-gray-900 text-white">
                          <p>Staff who have not started any learning activity yet.</p>
                        </TooltipContent>
                      </UITooltip>
                    </TooltipProvider>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-3xl font-bold text-orange-600">
                        {overviewData.staff_metrics.inactive_staff}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Click to view inactive</p>
                    </div>
                    <Users className="w-12 h-12 text-orange-400" />
                  </div>
                </Card>
                
                <Card 
                  className="p-4 bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleStaffBoxClick('archived')}
                >
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm text-gray-600">Archived Staff</p>
                    <TooltipProvider>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <button className="text-gray-400 hover:text-gray-600" onClick={(e) => e.stopPropagation()}>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs bg-gray-900 text-white">
                          <p>Staff removed or deactivated from active status.</p>
                        </TooltipContent>
                      </UITooltip>
                    </TooltipProvider>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-3xl font-bold text-gray-600">
                        {overviewData.staff_metrics.archived_staff}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">Click to view archived</p>
                    </div>
                    <Users className="w-12 h-12 text-gray-400" />
                  </div>
                </Card>
              </div>
              
              <div className="grid grid-cols-3 gap-6 mb-6">
                {/* Top 3 Departments by Score */}
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-bold text-gray-900">Top 3 Departments by Knowledge Excellence</h3>
                    <TooltipProvider>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <button className="text-gray-400 hover:text-gray-600">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs bg-gray-900 text-white">
                          <p>This widget shows the three departments with the highest average composite performance scores. Composite = 40% training completion + 40% assessment performance + 20% manager rating.</p>
                        </TooltipContent>
                      </UITooltip>
                    </TooltipProvider>
                  </div>
                  <p className="text-sm text-gray-500 mb-6">Sorted by average composite score</p>
                  
                  {overviewData.top_departments && overviewData.top_departments.length > 0 ? (
                    <div className="space-y-4">
                      {overviewData.top_departments.slice(0, 3).map((dept, index) => (
                        <div key={index} className="border-b border-gray-100 last:border-0 pb-4 last:pb-0">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-bold text-gray-400">{index + 1}.</span>
                              <h4 className="text-sm font-medium text-gray-900">{dept.name}</h4>
                            </div>
                            <span className="text-sm font-medium text-blue-600">
                              {Math.round(dept.score)}%
                            </span>
                          </div>
                          
                          <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="absolute top-0 left-0 h-full bg-blue-500 rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(dept.score, 100)}%` }}
                            />
                          </div>
                          
                          <p className="text-xs text-gray-500 mt-1">
                            {dept.employee_count || 0} staff • {dept.total_assessments || 0} assessments
                          </p>
                        </div>
                      ))}
                      
                      <button 
                        className="w-full text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center justify-center gap-2 mt-4 pt-4 border-t border-gray-200"
                        onClick={() => handleDepartmentListClick('desc')}
                      >
                        See Full Department List
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <p className="text-gray-600 font-medium">No department data available</p>
                      <p className="text-gray-500 text-sm mt-1">Department completion data will appear once staff activities or records are generated</p>
                    </div>
                  )}
                </Card>
                
                {/* Bottom 3 Departments by Score */}
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-xl font-bold text-gray-900">Departments Needing Knowledge Improvement</h3>
                    <TooltipProvider>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <button className="text-gray-400 hover:text-gray-600">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs bg-gray-900 text-white">
                          <p>This widget shows the three departments with the lowest average composite performance scores. Composite = 40% training completion + 40% assessment performance + 20% manager rating.</p>
                        </TooltipContent>
                      </UITooltip>
                    </TooltipProvider>
                  </div>
                  <p className="text-sm text-gray-500 mb-6">Departments needing knowledge improvement</p>
                  
                  {overviewData.bottom_departments && overviewData.bottom_departments.length > 0 ? (
                    <div className="space-y-4">
                      {overviewData.bottom_departments.slice(0, 3).map((dept, index) => (
                        <div key={index} className="border-b border-gray-100 last:border-0 pb-4 last:pb-0">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-lg font-bold text-gray-400">{index + 1}.</span>
                              <h4 className="text-sm font-medium text-gray-900">{dept.name}</h4>
                            </div>
                            <span className="text-sm font-medium text-orange-600">
                              {Math.round(dept.score)}%
                            </span>
                          </div>
                          
                          <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="absolute top-0 left-0 h-full bg-orange-500 rounded-full transition-all duration-500"
                              style={{ width: `${Math.min(dept.score, 100)}%` }}
                            />
                          </div>
                          
                          <p className="text-xs text-gray-500 mt-1">
                            {dept.employee_count || 0} staff • {dept.total_assessments || 0} assessments
                          </p>
                        </div>
                      ))}
                      
                      <button 
                        className="w-full text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center justify-center gap-2 mt-4 pt-4 border-t border-gray-200"
                        onClick={() => handleDepartmentListClick('asc')}
                      >
                        See Full Department List
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        </svg>
                      </div>
                      <p className="text-gray-600 font-medium">No department data available</p>
                      <p className="text-gray-500 text-sm mt-1">Department completion data will appear once staff activities or records are generated</p>
                    </div>
                  )}
                </Card>
                
                {/* Top Performers */}
                <Card className="p-6 bg-gradient-to-br from-yellow-50 to-yellow-100">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-lg">Top Performers</h3>
                    <TooltipProvider>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <button className="text-gray-400 hover:text-gray-600 transition-colors">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs bg-gray-900 text-white">
                          <p>Top Performer is calculated using a composite performance score: 40% training completion, 40% assessment performance, and 20% reporting manager rating.</p>
                        </TooltipContent>
                      </UITooltip>
                    </TooltipProvider>
                  </div>
                  
                  {overviewData.top_performers && overviewData.top_performers.length > 0 ? (
                    <div className="space-y-4">
                      {/* Top Performer - Circular Center-Aligned (60% of space) */}
                      <div className="bg-white rounded-full shadow-md border-2 border-yellow-400 p-6 flex items-center justify-center">
                        <div className="text-center">
                          <div className="flex justify-center mb-3">
                            <div className="w-19 h-19 rounded-full bg-yellow-100 flex items-center justify-center">
                              <Award className="w-11 h-11 text-yellow-500" />
                            </div>
                          </div>
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <span className="text-xs font-semibold text-yellow-600 bg-yellow-100 px-2 py-1 rounded">TOP PERFORMER</span>
                          </div>
                          <h4 className="text-xl font-bold text-gray-900 mb-1">
                            {overviewData.top_performers[0].name}
                          </h4>
                          <p className="text-sm text-gray-600 mb-3">{overviewData.top_performers[0].department}</p>
                          <div className="flex flex-col items-center">
                            <p className="text-xs text-gray-500 mb-1">Average Score</p>
                            <p className="text-3xl font-bold text-green-600">
                              {overviewData.top_performers[0].avg_score}%
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Runner-up */}
                      {overviewData.top_performers.length > 1 && (
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
                          <div className="flex items-center gap-3">
                            <Trophy className="w-8 h-8 text-gray-400 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="text-xs text-gray-500 mb-1">Runner-up</p>
                              <h5 className="text-sm font-semibold text-gray-900">
                                {overviewData.top_performers[1].name}
                              </h5>
                              <p className="text-xs text-gray-500">{overviewData.top_performers[1].department}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-lg font-bold text-green-600">
                                {overviewData.top_performers[1].avg_score}%
                              </p>
                              <p className="text-xs text-gray-500">Avg Score</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* View all performers link */}
                      <div className="text-center pt-2">
                        <button
                          onClick={() => handleTabChange('rewards')}
                          className="text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center justify-center gap-1 mx-auto transition-colors"
                        >
                          View all performers
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                      <p className="text-sm text-gray-500 mb-1">No top performers yet</p>
                      <p className="text-xs text-gray-400">Top performers will appear once staff complete assessments</p>
                    </div>
                  )}
                </Card>
              </div>
            </Card>
          )}

          {/* E. Audit & Compliance Analytics */}
          {activeTab === 'audit' && tabLoading.audit && <TabLoadingIndicator message="Loading Audit & Compliance data..." />}
          {activeTab === 'audit' && !tabLoading.audit && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h2 className="text-[24px] font-bold" style={{ color: '#0056B3' }}>Audit & Compliance Analytics</h2>
              </div>
              
              {/* Audit Readiness Legend */}
              <div className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <h4 className="font-semibold text-gray-800">Audit Readiness Guide</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-100">
                    <div className="w-4 h-4 rounded-full bg-red-500 mt-0.5 flex-shrink-0"></div>
                    <div>
                      <p className="font-semibold text-red-700 text-sm">Audit Critical</p>
                      <p className="text-xs text-red-600 mt-1">0-40% completion. Major training gaps with high audit risk.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg border border-orange-100">
                    <div className="w-4 h-4 rounded-full bg-orange-500 mt-0.5 flex-shrink-0"></div>
                    <div>
                      <p className="font-semibold text-orange-700 text-sm">Partially Audit Ready</p>
                      <p className="text-xs text-orange-600 mt-1">41-99% completion. Progressing but not fully audit-ready.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-100">
                    <div className="w-4 h-4 rounded-full bg-green-500 mt-0.5 flex-shrink-0"></div>
                    <div>
                      <p className="font-semibold text-green-700 text-sm">Audit Ready</p>
                      <p className="text-xs text-green-600 mt-1">100% completion. Fully prepared for audit review.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Two Widgets Side by Side */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Audit Readiness Status Widget */}
                <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold">Audit Readiness Status</h3>
                  <TooltipProvider>
                    <UITooltip>
                      <TooltipTrigger asChild>
                        <button className="text-gray-400 hover:text-gray-600">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs bg-gray-900 text-white">
                        <div className="space-y-2 text-sm">
                          <p className="font-semibold">Audit Readiness Guide</p>
                          <p>Audit readiness is calculated based on training completion status across all departments.</p>
                          <div className="space-y-1 mt-2 text-xs">
                            <div className="flex items-start gap-2">
                              <div className="w-2 h-2 rounded-full bg-green-400 mt-1 flex-shrink-0"></div>
                              <div><strong>Audit Ready:</strong> 100% of required trainings completed. Fully prepared for audits.</div>
                            </div>
                            <div className="flex items-start gap-2">
                              <div className="w-2 h-2 rounded-full bg-orange-400 mt-1 flex-shrink-0"></div>
                              <div><strong>Partially Audit Ready:</strong> 41-99% completed. Progressing but not fully audit-ready.</div>
                            </div>
                            <div className="flex items-start gap-2">
                              <div className="w-2 h-2 rounded-full bg-red-400 mt-1 flex-shrink-0"></div>
                              <div><strong>Audit Critical:</strong> 0-40% completed. Major training gaps with high audit risk.</div>
                            </div>
                          </div>
                          <p className="mt-2 pt-2 border-t border-gray-600 text-xs">Click on any department count to see detailed breakdown.</p>
                        </div>
                      </TooltipContent>
                    </UITooltip>
                  </TooltipProvider>
                </div>
                
                {auditData && auditData.compliance_summary ? (
                  <div>
                    {/* Modern Arc-Based Audit Readiness Gauge */}
                    <div className="flex flex-col items-center mb-6 px-4 pt-2 pb-4">
                      {(() => {
                        const percentage = auditData.compliance_summary.overall_percentage || 0;
                        const radius = 80;
                        const strokeWidth = 14;
                        const circumference = Math.PI * radius; // Half circle
                        const progress = (percentage / 100) * circumference;
                        
                        // Determine color based on percentage
                        const getColor = () => {
                          if (percentage === 100) return { primary: '#10B981', secondary: '#34D399', bg: '#D1FAE5' };
                          if (percentage >= 41) return { primary: '#F59E0B', secondary: '#FBBF24', bg: '#FEF3C7' };
                          return { primary: '#EF4444', secondary: '#F87171', bg: '#FEE2E2' };
                        };
                        const colors = getColor();
                        
                        return (
                          <>
                            <div className="relative w-56 h-32">
                              <svg viewBox="0 0 200 110" className="w-full h-full overflow-visible">
                                <defs>
                                  {/* Smooth gradient for progress arc */}
                                  <linearGradient id="auditProgressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                    <stop offset="0%" stopColor={colors.secondary} />
                                    <stop offset="50%" stopColor={colors.primary} />
                                    <stop offset="100%" stopColor={colors.primary} />
                                  </linearGradient>
                                  {/* Glow effect */}
                                  <filter id="auditGlow" x="-50%" y="-50%" width="200%" height="200%">
                                    <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                                    <feMerge>
                                      <feMergeNode in="coloredBlur"/>
                                      <feMergeNode in="SourceGraphic"/>
                                    </feMerge>
                                  </filter>
                                </defs>
                                
                                {/* Background arc track */}
                                <path
                                  d="M 20 100 A 80 80 0 0 1 180 100"
                                  fill="none"
                                  stroke="#F3F4F6"
                                  strokeWidth={strokeWidth}
                                  strokeLinecap="round"
                                />
                                
                                {/* Subtle zone indicators */}
                                <path
                                  d="M 20 100 A 80 80 0 0 1 47.6 38.1"
                                  fill="none"
                                  stroke="#FEE2E2"
                                  strokeWidth={strokeWidth}
                                  strokeLinecap="round"
                                  opacity="0.6"
                                />
                                <path
                                  d="M 51 34.5 A 80 80 0 0 1 178.5 96"
                                  fill="none"
                                  stroke="#FEF3C7"
                                  strokeWidth={strokeWidth}
                                  opacity="0.6"
                                />
                                <path
                                  d="M 179 97 A 80 80 0 0 1 180 100"
                                  fill="none"
                                  stroke="#D1FAE5"
                                  strokeWidth={strokeWidth}
                                  strokeLinecap="round"
                                  opacity="0.6"
                                />
                                
                                {/* Progress arc */}
                                <path
                                  d="M 20 100 A 80 80 0 0 1 180 100"
                                  fill="none"
                                  stroke="url(#auditProgressGradient)"
                                  strokeWidth={strokeWidth}
                                  strokeLinecap="round"
                                  strokeDasharray={`${progress} ${circumference}`}
                                  filter="url(#auditGlow)"
                                  style={{ transition: 'stroke-dasharray 0.8s ease-out' }}
                                />
                                
                                {/* Progress indicator dot */}
                                {(() => {
                                  const angle = Math.PI - (percentage / 100) * Math.PI;
                                  const dotX = 100 + radius * Math.cos(angle);
                                  const dotY = 100 - radius * Math.sin(angle);
                                  return (
                                    <g>
                                      <circle cx={dotX} cy={dotY} r="10" fill="white" filter="url(#auditGlow)" />
                                      <circle cx={dotX} cy={dotY} r="7" fill={colors.primary} />
                                      <circle cx={dotX} cy={dotY} r="3" fill="white" opacity="0.8" />
                                    </g>
                                  );
                                })()}
                                
                                {/* Minimal scale markers */}
                                <text x="15" y="115" fontSize="10" fill="#9CA3AF" fontWeight="500">0%</text>
                                <text x="185" y="115" fontSize="10" fill="#9CA3AF" fontWeight="500" textAnchor="end">100%</text>
                              </svg>
                              
                              {/* Center content */}
                              <div className="absolute inset-0 flex flex-col items-center justify-end pb-1">
                                <span className="text-4xl font-bold tracking-tight" style={{ color: colors.primary }}>
                                  {percentage}%
                                </span>
                              </div>
                            </div>
                            
                            {/* Status label and description */}
                            <div className="text-center mt-3">
                              <p className="text-sm text-gray-500 mb-2">Overall Audit Readiness</p>
                              <span 
                                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-full"
                                style={{ 
                                  backgroundColor: colors.bg, 
                                  color: colors.primary,
                                  boxShadow: `0 0 0 1px ${colors.primary}20`
                                }}
                              >
                                <span 
                                  className="w-2 h-2 rounded-full animate-pulse"
                                  style={{ backgroundColor: colors.primary }}
                                ></span>
                                {percentage === 100 
                                  ? 'Audit Ready' 
                                  : percentage >= 41 
                                    ? 'Partially Audit Ready' 
                                    : 'Audit Critical'}
                              </span>
                            </div>
                          </>
                        );
                      })()}
                    </div>

                    {/* Department Compliance Breakdown */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-gray-700 mb-4">Department Breakdown</h4>
                      
                      <div 
                        className="flex items-center justify-between p-4 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors border border-gray-200"
                        onClick={() => {
                          setComplianceModalOpen(true);
                          setComplianceModalType('fully');
                          setComplianceModalData(auditData.compliance_summary.fully_compliant_departments);
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded-full bg-green-500"></div>
                          <span className="text-base text-gray-700 font-medium">Audit Ready</span>
                          <span className="text-xs text-gray-500">(100% completion)</span>
                        </div>
                        <span className="text-lg font-bold text-green-600 hover:underline">
                          {auditData.compliance_summary.fully_compliant_count}
                        </span>
                      </div>

                      <div 
                        className="flex items-center justify-between p-4 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors border border-gray-200"
                        onClick={() => {
                          setComplianceModalOpen(true);
                          setComplianceModalType('partially');
                          setComplianceModalData(auditData.compliance_summary.partially_compliant_departments);
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded-full bg-orange-500"></div>
                          <span className="text-base text-gray-700 font-medium">Partially Audit Ready</span>
                          <span className="text-xs text-gray-500">(41-99% completion)</span>
                        </div>
                        <span className="text-lg font-bold text-orange-600 hover:underline">
                          {auditData.compliance_summary.partially_compliant_count}
                        </span>
                      </div>

                      <div 
                        className="flex items-center justify-between p-4 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors border border-gray-200"
                        onClick={() => {
                          setComplianceModalOpen(true);
                          setComplianceModalType('non');
                          setComplianceModalData(auditData.compliance_summary.non_compliant_departments);
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-4 h-4 rounded-full bg-red-500"></div>
                          <span className="text-base text-gray-700 font-medium">Audit Critical</span>
                          <span className="text-xs text-gray-500">(0-40% completion)</span>
                        </div>
                        <span className="text-lg font-bold text-red-600 hover:underline">
                          {auditData.compliance_summary.non_compliant_count}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No data available yet. This report will appear once activity/records are generated.</p>
                  </div>
                )}
              </Card>

              {/* Department Audit Readiness Ranking Widget */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold">Department Audit Readiness Ranking</h3>
                  <TooltipProvider>
                    <UITooltip>
                      <TooltipTrigger asChild>
                        <button className="text-gray-400 hover:text-gray-600">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs bg-gray-900 text-white">
                        <p className="text-sm">This widget ranks departments by audit readiness. Departments are ordered from highest to lowest training completion percentage.</p>
                        <div className="mt-2 pt-2 border-t border-gray-600 text-xs space-y-1">
                          <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-green-400"></div><span><strong>Audit Ready:</strong> 100%</span></div>
                          <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-orange-400"></div><span><strong>Partially Audit Ready:</strong> 41-99%</span></div>
                          <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-red-400"></div><span><strong>Audit Critical:</strong> 0-40%</span></div>
                        </div>
                      </TooltipContent>
                    </UITooltip>
                  </TooltipProvider>
                </div>

                {auditData && auditData.readiness_status && auditData.readiness_status.length > 0 ? (
                  <div>
                    {/* Ranked Department List */}
                    <div className="space-y-3">
                      {(() => {
                        // Filter departments with completion > 60%
                        const healthyDepts = auditData.readiness_status.filter(dept => dept.completion_percentage > 60);
                        
                        // Sort by completion percentage (descending - highest first)
                        const sortedDepts = [...healthyDepts].sort((a, b) => 
                          b.completion_percentage - a.completion_percentage
                        );
                        
                        // Show top 5 or all if showAllDepartments is true
                        const displayDepts = showAllDepartments ? sortedDepts : sortedDepts.slice(0, 5);
                        
                        // If no departments with >60%, show message
                        if (sortedDepts.length === 0) {
                          return (
                            <div className="text-center py-12">
                              <p className="text-gray-500">Data is not available at the moment. This report will be displayed once relevant activity or records are generated.</p>
                            </div>
                          );
                        }
                        
                        return displayDepts.map((dept, index) => {
                          const rank = sortedDepts.indexOf(dept) + 1;
                          let bgColor = 'bg-white';
                          let borderColor = 'border-gray-200';
                          let rankBadgeColor = 'bg-gray-100 text-gray-700';
                          
                          if (rank <= 3) {
                            bgColor = 'bg-gradient-to-r from-green-50 to-emerald-50';
                            borderColor = 'border-green-300';
                            rankBadgeColor = 'bg-green-500 text-white';
                          } else if (rank <= 5) {
                            bgColor = 'bg-gradient-to-r from-blue-50 to-indigo-50';
                            borderColor = 'border-blue-200';
                            rankBadgeColor = 'bg-blue-500 text-white';
                          }
                          
                          return (
                            <div 
                              key={dept.department_id}
                              className={`flex items-center justify-between p-4 rounded-lg border-2 ${bgColor} ${borderColor} transition-all hover:shadow-md`}
                            >
                              <div className="flex items-center gap-4 flex-1">
                                <div className={`w-8 h-8 rounded-full ${rankBadgeColor} flex items-center justify-center font-bold text-sm flex-shrink-0`}>
                                  {rank}
                                </div>
                                <div className="flex-1">
                                  <p className="font-semibold text-gray-900">{dept.department_name}</p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {dept.completed_count} of {dept.required_count} trainings completed
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="w-24 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className={`h-2 rounded-full transition-all ${
                                      dept.completion_percentage === 100 ? 'bg-green-500' :
                                      dept.completion_percentage >= 41 ? 'bg-orange-500' : 'bg-red-500'
                                    }`}
                                    style={{ width: `${dept.completion_percentage}%` }}
                                  ></div>
                                </div>
                                <span className={`text-lg font-bold min-w-[60px] text-right ${
                                  dept.completion_percentage === 100 ? 'text-green-600' :
                                  dept.completion_percentage >= 41 ? 'text-orange-600' : 'text-red-600'
                                }`}>
                                  {dept.completion_percentage}%
                                </span>
                              </div>
                            </div>
                          );
                        });
                      })()}
                    </div>

                    {(() => {
                      // Calculate filtered healthy departments count
                      const healthyDeptsCount = auditData.readiness_status.filter(dept => dept.completion_percentage > 60).length;
                      
                      return healthyDeptsCount > 5 && (
                        <div className="mt-4 text-center">
                          <button
                            onClick={() => setShowAllDepartments(!showAllDepartments)}
                            className="px-6 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                          >
                            {showAllDepartments ? (
                              <>
                                <span>Show Less</span>
                                <svg className="w-4 h-4 inline-block ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                </svg>
                              </>
                            ) : (
                              <>
                                <span>View All ({healthyDeptsCount} departments)</span>
                                <svg className="w-4 h-4 inline-block ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </>
                            )}
                          </button>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500">Data is not available at the moment. This report will be displayed once relevant activity or records are generated.</p>
                  </div>
                )}
              </Card>
              </div>

              {/* Audit Critical Departments Widget */}
              <Card className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold">Audit Critical Departments</h3>
                  <TooltipProvider>
                    <UITooltip>
                      <TooltipTrigger asChild>
                        <button className="text-gray-400 hover:text-gray-600">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs bg-gray-900 text-white">
                        <p className="text-sm">This widget lists departments in the Audit Critical zone (0-40% completion). These departments have significant training gaps and high audit risk requiring immediate corrective training action.</p>
                      </TooltipContent>
                    </UITooltip>
                  </TooltipProvider>
                </div>

                {auditData && auditData.risky_departments && auditData.risky_departments.length > 0 ? (
                  <div>
                    {/* Risky Department List */}
                    <div className="space-y-3">
                      {auditData.risky_departments.slice(0, 5).map((dept) => (
                        <div 
                          key={dept.department_id}
                          className="flex items-center justify-between p-4 rounded-lg border-2 border-red-200 bg-gradient-to-r from-red-50 to-orange-50 transition-all hover:shadow-md"
                        >
                          <div className="flex items-center gap-4 flex-1">
                            {/* Alert Icon */}
                            <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                            </div>
                            
                            {/* Department Name */}
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900">{dept.department_name}</p>
                              {dept.completion_percentage === 0 ? (
                                <span className="inline-block mt-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-full">
                                  Not Started
                                </span>
                              ) : (
                                <p className="text-xs text-gray-500 mt-1">
                                  {dept.completed_count} of {dept.required_count} trainings completed
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {/* Compliance Percentage */}
                          <div className="flex items-center gap-3">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div 
                                className="h-2 rounded-full bg-red-500 transition-all"
                                style={{ width: `${dept.completion_percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-lg font-bold min-w-[60px] text-right text-red-600">
                              {dept.completion_percentage}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* View More Button */}
                    {auditData.risky_departments.length > 5 && (
                      <div className="mt-4 text-center">
                        <button
                          onClick={() => setRiskyDepartmentsModalOpen(true)}
                          className="px-6 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                        >
                          View More Departments
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500">Data is not available at the moment. This report will be displayed once relevant activity or records are generated.</p>
                  </div>
                )}
              </Card>
            </div>
          )}
          
          {/* F. Clinical Outcomes */}
          {activeTab === 'patient' && tabLoading.patient && <TabLoadingIndicator message="Loading Clinical Outcomes data..." />}
          {activeTab === 'patient' && !tabLoading.patient && patientCareData && (
            <div className="space-y-6">
              <h2 className="text-[24px] font-bold" style={{ color: '#0056B3' }}>Clinical Outcomes</h2>
              
              {/* Clinical Outcomes Guide - Collapsible */}
              <div className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-xl p-4">
                <div 
                  className="flex items-start gap-3 cursor-pointer"
                  onClick={() => setClinicalGuideExpanded(!clinicalGuideExpanded)}
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-800">Clinical Outcomes Guide</h4>
                      <button className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1">
                        {clinicalGuideExpanded ? (
                          <>
                            <span>Show less</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                          </>
                        ) : (
                          <>
                            <span>Read more</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </>
                        )}
                      </button>
                    </div>
                    <p className="text-sm text-gray-600 mt-2 leading-relaxed">
                      {clinicalGuideExpanded ? (
                        "The insights are based on internal surveys and structured feedback initiated by hospital administration and completed by reporting managers, reflecting observed learning impact within the system, and are intended solely for internal quality improvement and training assessment, not as a measure of clinical competency, formal staff appraisal, or departmental performance."
                      ) : (
                        "Insights are based on internal surveys and feedback from reporting managers, intended for quality improvement and training assessment."
                      )}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Hospital Patient Care Sentiment Trend */}
              <Card className="p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Hospital Patient Care Sentiment Trend</h3>
                  
                  <div className="flex items-center space-x-4">
                    {/* Info Icon - Top Right */}
                    <TooltipProvider>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <button className="text-gray-400 hover:text-gray-600" onClick={(e) => e.stopPropagation()}>
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-sm bg-gray-900 text-white">
                          <p>This trend reflects the hospital's overall patient care sentiment based on structured evaluations submitted by reporting managers for their team members. Scores are averaged across all evaluations for each month to indicate whether patient care outcomes are improving, stable, or require attention over time. This is an internal quality indicator and not an individual staff performance rating.</p>
                        </TooltipContent>
                      </UITooltip>
                    </TooltipProvider>
                  </div>
                </div>
                
                {patientCareImpactLoading ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">The data is loading…</p>
                  </div>
                ) : patientCareImpactData?.trend_data && patientCareImpactData.trend_data.length > 0 ? (
                  <div>
                    {/* Dot Trend Graph - Numeric Y-Axis (1 to 4) */}
                    <div className="mb-6">
                      <div className="relative" style={{ height: '220px' }}>
                        {/* Y-Axis Labels (Numeric scale 1-4 with sentiment labels) */}
                        <div className="absolute left-0 top-0 bottom-8 w-36 flex flex-col justify-between py-1 pr-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-xs text-green-600 font-medium">Strongly Positive</span>
                            <span className="text-xs text-gray-500">(4.0)</span>
                          </div>
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-xs text-green-600 font-medium"></span>
                            <span className="text-xs text-gray-400">(3.5)</span>
                          </div>
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-xs text-blue-600 font-medium">Positive</span>
                            <span className="text-xs text-gray-400">(3.0)</span>
                          </div>
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-xs text-blue-600 font-medium"></span>
                            <span className="text-xs text-gray-400">(2.5)</span>
                          </div>
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-xs text-orange-600 font-medium">Neutral</span>
                            <span className="text-xs text-gray-400">(2.0)</span>
                          </div>
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-xs text-orange-600 font-medium"></span>
                            <span className="text-xs text-gray-400">(1.5)</span>
                          </div>
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-xs text-red-600 font-medium">Needs Improvement</span>
                            <span className="text-xs text-gray-500">(1.0)</span>
                          </div>
                        </div>
                        
                        {/* Graph Area */}
                        <div className="ml-36 h-full border-l-2 border-b-2 border-gray-400 relative" style={{ height: 'calc(100% - 32px)' }}>
                          {/* Horizontal Grid Lines at each 0.5 increment */}
                          <div className="absolute w-full border-t border-gray-200" style={{ bottom: '100%' }}></div>
                          <div className="absolute w-full border-t border-dashed border-gray-200" style={{ bottom: '83.33%' }}></div>
                          <div className="absolute w-full border-t border-gray-200" style={{ bottom: '66.67%' }}></div>
                          <div className="absolute w-full border-t border-dashed border-gray-200" style={{ bottom: '50%' }}></div>
                          <div className="absolute w-full border-t border-gray-200" style={{ bottom: '33.33%' }}></div>
                          <div className="absolute w-full border-t border-dashed border-gray-200" style={{ bottom: '16.67%' }}></div>
                          
                          {/* Dots - One per month */}
                          <div className="absolute inset-0">
                            {patientCareImpactData.trend_data.map((item, idx) => {
                              const score = parseFloat(item.index) || 0;
                              const totalMonths = patientCareImpactData.trend_data.length;
                              
                              // Calculate X position (evenly distributed)
                              const xPercent = totalMonths === 1 ? 50 : (idx / (totalMonths - 1)) * 80 + 10;
                              
                              // Calculate Y position based on numeric scale (1 to 4)
                              // Bottom (0%) = 1.0, Top (100%) = 4.0
                              // Formula: yPercent = ((score - 1) / 3) * 100
                              const yPercent = Math.max(0, Math.min(100, ((score - 1) / 3) * 100));
                              
                              // Determine dot color based on sentiment thresholds
                              let dotColor;
                              let sentiment;
                              if (score >= 3.5) {
                                dotColor = '#22c55e'; // green-500
                                sentiment = 'Strongly Positive';
                              } else if (score >= 2.5) {
                                dotColor = '#3b82f6'; // blue-500
                                sentiment = 'Positive';
                              } else if (score >= 1.5) {
                                dotColor = '#f97316'; // orange-500
                                sentiment = 'Neutral';
                              } else {
                                dotColor = '#ef4444'; // red-500
                                sentiment = 'Needs Improvement';
                              }
                              
                              // Determine tooltip position based on dot position
                              // If dot is on right side (>60%), position tooltip to the left
                              // If dot is on left side (<40%), position tooltip to the right
                              // Otherwise center it
                              let tooltipAlign = 'center';
                              let tooltipTransform = 'translateX(-50%)';
                              let arrowAlign = 'center';
                              
                              if (xPercent > 65) {
                                tooltipAlign = 'right';
                                tooltipTransform = 'translateX(-85%)';
                                arrowAlign = 'right';
                              } else if (xPercent < 35) {
                                tooltipAlign = 'left';
                                tooltipTransform = 'translateX(-15%)';
                                arrowAlign = 'left';
                              }
                              
                              return (
                                <div 
                                  key={idx}
                                  className="absolute group"
                                  style={{ 
                                    left: `${xPercent}%`, 
                                    bottom: `${yPercent}%`,
                                    transform: 'translate(-50%, 50%)'
                                  }}
                                >
                                  {/* Tooltip - appears on hover with boundary-aware positioning */}
                                  <div 
                                    className="absolute bottom-full mb-4 hidden group-hover:flex z-50 pointer-events-none"
                                    style={{
                                      left: '50%',
                                      transform: tooltipTransform,
                                      flexDirection: 'column',
                                      alignItems: arrowAlign === 'left' ? 'flex-start' : arrowAlign === 'right' ? 'flex-end' : 'center'
                                    }}
                                  >
                                    {/* Tooltip Container - Full background wrapper */}
                                    <div 
                                      className="tooltip-box"
                                      style={{
                                        display: 'inline-block',
                                        backgroundColor: '#1f2937',
                                        color: '#ffffff',
                                        padding: '14px 18px',
                                        borderRadius: '10px',
                                        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
                                        fontSize: '13px',
                                        lineHeight: '1.7',
                                        minWidth: '160px',
                                        maxWidth: '260px',
                                        boxSizing: 'border-box'
                                      }}
                                    >
                                      <div style={{ fontWeight: '700', marginBottom: '10px', fontSize: '15px', borderBottom: '1px solid #4b5563', paddingBottom: '10px', color: '#ffffff' }}>
                                        {item.period}
                                      </div>
                                      <div style={{ marginBottom: '8px', color: '#e5e7eb' }}>
                                        Score: <span style={{ fontWeight: '700', color: '#60a5fa' }}>{score}</span>
                                      </div>
                                      <div style={{ marginBottom: '8px', color: '#e5e7eb' }}>
                                        Sentiment: <span style={{ fontWeight: '700', color: '#ffffff' }}>{sentiment}</span>
                                      </div>
                                      {item.evaluation_count && (
                                        <div style={{ marginBottom: '0', color: '#e5e7eb' }}>
                                          Evaluations: <span style={{ fontWeight: '700', color: '#ffffff' }}>{item.evaluation_count}</span>
                                        </div>
                                      )}
                                    </div>
                                    {/* Arrow pointer */}
                                    <div 
                                      style={{
                                        width: '0',
                                        height: '0',
                                        borderLeft: '8px solid transparent',
                                        borderRight: '8px solid transparent',
                                        borderTop: '10px solid #1f2937',
                                        marginLeft: arrowAlign === 'left' ? '24px' : arrowAlign === 'right' ? '0' : '0',
                                        marginRight: arrowAlign === 'right' ? '24px' : arrowAlign === 'left' ? '0' : '0',
                                        alignSelf: arrowAlign === 'center' ? 'center' : undefined
                                      }}
                                    ></div>
                                  </div>
                                  
                                  {/* The Dot */}
                                  <div 
                                    className="w-5 h-5 rounded-full cursor-pointer shadow-md hover:scale-125 transition-transform"
                                    style={{ 
                                      backgroundColor: dotColor,
                                      border: '2px solid white',
                                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}
                                  ></div>
                                </div>
                              );
                            })}
                          </div>
                          
                          {/* X-Axis Labels (below the graph) */}
                          <div className="absolute w-full flex justify-around" style={{ top: '100%', marginTop: '8px' }}>
                            {patientCareImpactData.trend_data.map((item, idx) => {
                              const totalMonths = patientCareImpactData.trend_data.length;
                              const xPercent = totalMonths === 1 ? 50 : (idx / (totalMonths - 1)) * 80 + 10;
                              return (
                                <span 
                                  key={idx} 
                                  className="text-xs text-gray-600 font-medium absolute transform -translate-x-1/2"
                                  style={{ left: `${xPercent}%` }}
                                >
                                  {item.period}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Month-wise Patient Care Averages - Text Display */}
                    <div className="flex flex-wrap items-center justify-center gap-2 py-4 border-t border-gray-200">
                      {patientCareImpactData.trend_data.map((item, idx) => {
                        const score = item.index;
                        const sentiment = score >= 3.5 ? 'Strongly Positive' : score >= 2.5 ? 'Positive' : score >= 1.5 ? 'Neutral' : 'Needs Improvement';
                        const sentimentStyle = score >= 3.5 ? 'bg-green-100 text-green-800' : score >= 2.5 ? 'bg-blue-100 text-blue-800' : score >= 1.5 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800';
                        
                        return (
                          <div key={idx} className="flex items-center gap-2">
                            {idx > 0 && <span className="text-gray-300 text-lg font-light">|</span>}
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-600 font-medium">{item.period}:</span>
                              <span className="text-xl font-bold text-blue-600">{score}</span>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${sentimentStyle}`}>
                                {sentiment}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Legend */}
                    <div className="flex justify-center items-center gap-4 text-xs mt-4">
                      <div className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-green-500"></span>
                        <span>3.5-4.0: Strongly Positive</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                        <span>2.5-3.49: Positive</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                        <span>1.5-2.49: Neutral</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-red-500"></span>
                        <span>1.0-1.49: Needs Improvement</span>
                      </div>
                    </div>
                    
                    <p className="text-center text-xs text-gray-400 mt-3">
                      Total staff evaluations: {patientCareImpactData.total_evaluations} | Data since hospital registration
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500">Data is not available at the moment. This report will be displayed once relevant activity or records are generated.</p>
                  </div>
                )}
              </Card>
              
              {/* Department Outcome Comparison (from Evaluation Form) */}
              <Card className="p-6 mb-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900">Department Outcome Comparison</h3>
                  
                  {/* Info Icon - Top Right */}
                  <TooltipProvider>
                    <UITooltip>
                      <TooltipTrigger asChild>
                        <button className="text-gray-400 hover:text-gray-600" onClick={(e) => e.stopPropagation()}>
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs bg-gray-900 text-white">
                        <p>This widget compares departments based on the latest reporting manager evaluations of staff across compliance, skill competency, and patient care. Scores are averaged to reflect overall departmental outcomes.</p>
                      </TooltipContent>
                    </UITooltip>
                  </TooltipProvider>
                </div>
                
                {departmentOutcomesLoading ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">The data is loading…</p>
                  </div>
                ) : departmentOutcomesData?.department_outcomes && departmentOutcomesData.department_outcomes.length > 0 ? (
                  <div>
                    <div style={{ width: '100%', minHeight: 400 }}>
                      <BarChart 
                        width={900}
                        height={400}
                        data={departmentOutcomesData.department_outcomes} 
                        margin={{ top: 40, right: 30, left: 60, bottom: 80 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                        <XAxis 
                          dataKey="department" 
                          tick={{ fontSize: 11 }}
                          angle={-30}
                          textAnchor="end"
                          height={80}
                          tickLine={false}
                        />
                        <YAxis 
                          domain={[0, 4]}
                          ticks={[1, 2, 3, 4]}
                          tick={{ fontSize: 12 }}
                          tickLine={false}
                          label={{ value: 'Outcome Score (out of 4)', angle: -90, position: 'insideLeft', fontSize: 11, dx: -10 }}
                        />
                        <Tooltip 
                          cursor={{ fill: 'rgba(0,0,0,0.05)' }}
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <div className="bg-white p-4 border border-gray-200 rounded-lg shadow-lg min-w-[220px]">
                                  <p className="font-bold text-gray-900 mb-2">{data.department}</p>
                                  <div className="space-y-1 text-sm">
                                    <p className="flex justify-between">
                                      <span className="text-gray-600">Compliance:</span>
                                      <span className="font-semibold text-blue-600">{data.compliance_avg} / 4</span>
                                    </p>
                                    <p className="flex justify-between">
                                      <span className="text-gray-600">Skill Competency:</span>
                                      <span className="font-semibold text-green-600">{data.skill_avg} / 4</span>
                                    </p>
                                    <p className="flex justify-between">
                                      <span className="text-gray-600">Patient Care:</span>
                                      <span className="font-semibold text-purple-600">{data.patient_care_avg} / 4</span>
                                    </p>
                                    <hr className="my-1" />
                                    <p className="flex justify-between font-medium">
                                      <span className="text-gray-900">Overall Outcome:</span>
                                      <span className="font-bold text-orange-600">{data.outcome_avg} / 4</span>
                                    </p>
                                    <p className="text-xs text-gray-400 mt-1">Staff evaluated: {data.staff_evaluated}</p>
                                  </div>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Bar 
                          dataKey="outcome_avg" 
                          fill="#4CAF50" 
                          radius={[4, 4, 0, 0]}
                          name="Outcome Average"
                        />
                      </BarChart>
                    </div>
                    
                    <p className="text-center text-xs text-gray-400 mt-2">
                      Based on {departmentOutcomesData.total_departments} department(s) | Sorted by outcome score (descending) | Max score: 4
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500">Data is not available at the moment. This report will be displayed once evaluations are completed.</p>
                  </div>
                )}
              </Card>
            </div>
          )}
          
          {/* G. Staff Performance Insights */}
          {activeTab === 'performance' && tabLoading.performance && <TabLoadingIndicator message="Loading Staff Performance data..." />}
          {activeTab === 'performance' && !tabLoading.performance && performanceData && (
            <Card className="p-6">
              <h2 className="text-[24px] font-bold mb-6" style={{ color: '#0056B3' }}>Staff Performance Insights</h2>
              
              {/* Staff Performance Distribution (Composite Score) Widget - MOVED TO TOP */}
              <Card className="p-6 mb-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold">Staff Performance Distribution (Composite Score)</h3>
                  <TooltipProvider>
                    <UITooltip>
                      <TooltipTrigger asChild>
                        <button className="text-gray-400 hover:text-gray-600">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs bg-gray-900 text-white">
                        <p className="text-sm">This widget shows overall staff performance using a weighted composite score based on completion (40%), assessment scores (40%), and manager ratings (20%). It helps identify staff needing attention and top performers.</p>
                      </TooltipContent>
                    </UITooltip>
                  </TooltipProvider>
                </div>
                
                {staffPerfDistLoading ? (
                  <div className="text-center py-12">
                    <div className="relative inline-block">
                      <div className="w-8 h-8 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
                    </div>
                    <p className="text-gray-500 mt-4">The data is loading…</p>
                  </div>
                ) : staffPerfDistData && staffPerfDistData.total_staff > 0 ? (
                  <div className="border border-gray-200 rounded p-4 bg-white">
                    <div className="flex justify-center relative">
                      <BarChart 
                        width={600} 
                        height={320} 
                        data={staffPerfDistData.distribution}
                        margin={{ top: 25, right: 20, left: 20, bottom: 5 }}
                        onMouseLeave={() => setHoveredPerfBandIndex(null)}
                      >
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis 
                          dataKey="band" 
                          tick={{ fontSize: 12, fontWeight: 600 }}
                          tickLine={false}
                          axisLine={{ stroke: '#E5E7EB' }}
                        />
                        <YAxis 
                          tick={{ fontSize: 12 }}
                          label={{ value: 'Staff Count', angle: -90, position: 'insideLeft', fontSize: 12 }}
                          allowDecimals={false}
                          tickLine={false}
                          axisLine={{ stroke: '#E5E7EB' }}
                        />
                        <Bar 
                          dataKey="count" 
                          onClick={(data) => handlePerfBandBarClick(data)}
                          cursor="pointer"
                          label={{ position: 'top', fontSize: 12, fontWeight: 'bold', fill: '#374151' }}
                          isAnimationActive={false}
                          barSize={80}
                        >
                          {staffPerfDistData.distribution.map((entry, index) => (
                            <Cell 
                              key={`perf-cell-top-${index}`} 
                              fill={entry.color}
                              onMouseEnter={() => setHoveredPerfBandIndex(index)}
                              onMouseLeave={() => setHoveredPerfBandIndex(null)}
                              style={{ cursor: 'pointer' }}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                      
                      {/* Custom Tooltip */}
                      {hoveredPerfBandIndex !== null && staffPerfDistData.distribution[hoveredPerfBandIndex] && (
                        <div 
                          className="absolute bg-white p-4 border border-gray-200 rounded-lg shadow-xl max-w-sm pointer-events-none"
                          style={{ 
                            zIndex: 9999,
                            left: `${70 + (hoveredPerfBandIndex * 140)}px`,
                            top: '50px'
                          }}
                        >
                          {(() => {
                            const bandData = staffPerfDistData.distribution[hoveredPerfBandIndex];
                            const staffList = bandData.staff || [];
                            const displayStaff = staffList.slice(0, 5);
                            const hasMore = staffList.length > 5;
                            
                            return (
                              <>
                                <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                                  <div 
                                    className="w-3 h-3 rounded-full" 
                                    style={{ backgroundColor: bandData.color }}
                                  ></div>
                                  <p className="font-bold text-gray-900">{bandData.band}</p>
                                </div>
                                <p className="text-sm text-gray-600 mb-2">
                                  <span className="font-semibold">{bandData.count}</span> staff member{bandData.count !== 1 ? 's' : ''}
                                </p>
                                {staffList.length > 0 && (
                                  <div className="max-h-40 overflow-y-auto">
                                    <p className="text-xs font-semibold text-gray-500 mb-1">Top Staff:</p>
                                    {displayStaff.map((staff, idx) => (
                                      <div key={idx} className="flex justify-between items-center py-1 text-xs border-b border-gray-100 last:border-0">
                                        <span className="text-gray-700 truncate pr-2" style={{ maxWidth: '140px' }}>{staff.name}</span>
                                        <span className="font-semibold" style={{ color: bandData.color }}>{staff.composite_score}%</span>
                                      </div>
                                    ))}
                                    {hasMore && (
                                      <p className="text-xs text-blue-600 mt-2 font-medium">
                                        + {staffList.length - 5} more (click to view all)
                                      </p>
                                    )}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                    
                    {/* Legend */}
                    <div className="flex justify-center gap-6 mt-4 pt-4 border-t">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                        <span className="text-xs text-gray-600">Needs Attention (0-40)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                        <span className="text-xs text-gray-600">Average (41-60)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                        <span className="text-xs text-gray-600">Good (61-80)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                        <span className="text-xs text-gray-600">Excellent (81-100)</span>
                      </div>
                    </div>
                    
                    <p className="text-center text-xs text-gray-400 mt-3">
                      Total: {staffPerfDistData.total_staff} staff | Weights: Completion {staffPerfDistData.weights?.completion * 100}%, Assessment {staffPerfDistData.weights?.assessment * 100}%, Manager Rating {staffPerfDistData.weights?.manager_rating * 100}% | Click on a bar to view staff details
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500">Data is not available at the moment. This report will be displayed once relevant activity or records are generated.</p>
                  </div>
                )}
              </Card>
              
              {/* Hospital Areas of Strength & Improvement */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                {/* Areas of Strength */}
                <Card className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                    <h3 className="text-lg font-bold text-green-600">Areas of Strength</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">Chapters with ≥95% hospital average assessment score</p>
                  
                  {areasStrengthImprovementLoading ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">The data is loading…</p>
                    </div>
                  ) : areasStrengthImprovementData?.strength_topics && areasStrengthImprovementData.strength_topics.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {areasStrengthImprovementData.strength_topics.map((topic, index) => (
                        <div 
                          key={topic.topic_id || index}
                          className="px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                          data-tooltip={`Average Score: ${topic.average_score}% | Staff Attempted: ${topic.staff_attempted}`}
                        >
                          <span className="text-sm text-gray-900">{topic.topic_name}</span>
                          <span className="text-xs text-green-600 ml-2">({topic.average_score}%)</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500 text-sm">Data is not available at the moment. This report will be displayed once relevant activity or records are generated.</p>
                    </div>
                  )}
                </Card>
                
                {/* Areas of Improvement */}
                <Card className="p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <TrendingDown className="w-6 h-6 text-orange-600" />
                    <h3 className="text-lg font-bold text-orange-600">Areas for Improvement</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">Chapters with ≤60% hospital average assessment score</p>
                  
                  {areasStrengthImprovementLoading ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">The data is loading…</p>
                    </div>
                  ) : areasStrengthImprovementData?.improvement_topics && areasStrengthImprovementData.improvement_topics.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {areasStrengthImprovementData.improvement_topics.map((topic, index) => (
                        <div 
                          key={topic.topic_id || index}
                          className="px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                          data-tooltip={`Average Score: ${topic.average_score}% | Staff Attempted: ${topic.staff_attempted}`}
                        >
                          <span className="text-sm text-gray-900">{topic.topic_name}</span>
                          <span className="text-xs text-orange-600 ml-2">({topic.average_score}%)</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500 text-sm">Data is not available at the moment. This report will be displayed once relevant activity or records are generated.</p>
                    </div>
                  )}
                </Card>
              </div>
            </Card>
          )}
          
          {/* H. Cost-Effectiveness Reports */}
          {activeTab === 'cost' && tabLoading.cost && <TabLoadingIndicator message="Loading Cost-Effectiveness data..." />}
          {activeTab === 'cost' && !tabLoading.cost && costEffectivenessData && (
            <Card className="p-6">
              <h2 className="text-[24px] font-bold mb-6" style={{ color: '#0056B3' }}>Cost-Effectiveness Reports</h2>
              
              <div className="grid grid-cols-2 gap-6 mb-6">
                {/* Pre-training Survey */}
                <div>
                  <h3 className="font-semibold mb-3 text-lg">Pre-Training Survey (Baseline)</h3>
                  {costEffectivenessData.pre_training.training_effectiveness > 0 || 
                   costEffectivenessData.pre_training.operational_efficiency > 0 ||
                   costEffectivenessData.pre_training.compliance_readiness > 0 ||
                   costEffectivenessData.pre_training.staff_satisfaction > 0 ? (
                    <RechartsResponsiveContainer width="100%" height={250}>
                      <RadarChart data={[
                        { metric: 'Training Effectiveness', value: costEffectivenessData.pre_training.training_effectiveness },
                        { metric: 'Operational Efficiency', value: costEffectivenessData.pre_training.operational_efficiency },
                        { metric: 'Compliance', value: costEffectivenessData.pre_training.compliance_readiness },
                        { metric: 'Staff Satisfaction', value: costEffectivenessData.pre_training.staff_satisfaction }
                      ]}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                        <PolarRadiusAxis angle={90} domain={[0, 5]} />
                        <Radar name="Pre-Training" dataKey="value" stroke="#F44336" fill="#F44336" fillOpacity={0.3} />
                        <Tooltip />
                      </RadarChart>
                    </RechartsResponsiveContainer>
                  ) : (
                    <PlaceholderWidget 
                      title="Pre-Training Survey" 
                      message="No pre-training survey data available"
                      height="250px"
                    />
                  )}
                </div>
                
                {/* Post-training Survey */}
                <div>
                  <h3 className="font-semibold mb-3 text-lg">Post-Training Survey (Impact)</h3>
                  {costEffectivenessData.post_training.training_effectiveness > 0 || 
                   costEffectivenessData.post_training.operational_efficiency > 0 ||
                   costEffectivenessData.post_training.compliance_readiness > 0 ||
                   costEffectivenessData.post_training.staff_satisfaction > 0 ? (
                    <RechartsResponsiveContainer width="100%" height={250}>
                      <RadarChart data={[
                        { metric: 'Training Effectiveness', value: costEffectivenessData.post_training.training_effectiveness },
                        { metric: 'Operational Efficiency', value: costEffectivenessData.post_training.operational_efficiency },
                        { metric: 'Compliance', value: costEffectivenessData.post_training.compliance_readiness },
                        { metric: 'Staff Satisfaction', value: costEffectivenessData.post_training.staff_satisfaction }
                      ]}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                        <PolarRadiusAxis angle={90} domain={[0, 5]} />
                        <Radar name="Post-Training" dataKey="value" stroke="#4CAF50" fill="#4CAF50" fillOpacity={0.3} />
                        <Tooltip />
                      </RadarChart>
                    </RechartsResponsiveContainer>
                  ) : (
                    <PlaceholderWidget 
                      title="Post-Training Survey" 
                      message="No post-training survey data available"
                      height="250px"
                    />
                  )}
                </div>
              </div>
              
              {/* Cost-Effectiveness Indicators */}
              <div>
                <h3 className="font-semibold mb-3 text-lg">Department-wise Cost-Effectiveness Indicators</h3>
                {costEffectivenessData.department_roi && costEffectivenessData.department_roi.length > 0 ? (
                  <div className="space-y-3">
                    {(costEffectivenessData.department_roi || []).map((dept, idx) => (
                      <div key={idx} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-lg">{dept.department}</p>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <p className="text-xs text-gray-600">Pre-Training</p>
                            <p className="text-lg font-bold text-red-600">{dept.pre.toFixed(1)}</p>
                          </div>
                          <div className="text-2xl text-gray-400">→</div>
                          <div className="text-center">
                            <p className="text-xs text-gray-600">Post-Training</p>
                            <p className="text-lg font-bold text-green-600">{dept.post.toFixed(1)}</p>
                          </div>
                          <div className="text-center bg-green-100 px-4 py-2 rounded-lg">
                            <p className="text-xs text-gray-600">Improvement</p>
                            <p className="text-xl font-bold text-green-600">+{dept.improvement}%</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                ) : (
                  <PlaceholderWidget 
                    title="Cost-Effectiveness Indicators" 
                    message="No cost-effectiveness data available"
                    height="200px"
                  />
                )}
              </div>
            </Card>
          )}
          
          {/* I. Feedback Analytics */}
          {activeTab === 'feedback' && tabLoading.feedback && <TabLoadingIndicator message="Loading Feedback data..." />}
          {activeTab === 'feedback' && !tabLoading.feedback && feedbackData && (
            <Card className="p-6">
              <h2 className="text-[24px] font-bold mb-6" style={{ color: '#0056B3' }}>Feedback Analytics</h2>
              
              <div className="grid grid-cols-2 gap-6">
                {/* Overall Feedback Summary */}
                <Card className="p-6 bg-gradient-to-br from-purple-50 to-purple-100">
                  <div className="flex justify-between items-start mb-6">
                    <h3 className="font-semibold text-lg flex-1 text-center">Overall Feedback Summary</h3>
                    <TooltipProvider>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <button className="text-gray-400 hover:text-gray-600">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs bg-gray-900 text-white">
                          <p>This widget shows the overall average feedback given across all chapters, and how many staff have submitted feedback.</p>
                        </TooltipContent>
                      </UITooltip>
                    </TooltipProvider>
                  </div>
                  
                  {feedbackData.average_rating ? (
                    <div className="flex flex-col items-center">
                      {/* Circular Feedback Indicator */}
                      <div className="relative inline-flex items-center justify-center mb-4 p-2">
                        <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 120 120">
                          <circle
                            cx="60"
                            cy="60"
                            r="52"
                            stroke="#e5e7eb"
                            strokeWidth="10"
                            fill="none"
                          />
                          <circle
                            cx="60"
                            cy="60"
                            r="52"
                            stroke="#8b5cf6"
                            strokeWidth="10"
                            fill="none"
                            strokeDasharray={`${(feedbackData.average_rating / 5) * 326.7} 326.7`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-3xl font-bold text-purple-600">
                            {feedbackData.average_rating}
                          </span>
                          <span className="text-xs text-gray-600">/ 5</span>
                        </div>
                      </div>
                      
                      <p className="text-lg font-semibold text-gray-800 mb-3">Average Rating</p>
                      
                      {/* Star Rating */}
                      <div className="mb-4">
                        {renderStars(feedbackData.average_rating)}
                      </div>
                      
                      {/* Feedback Count */}
                      <div className="mt-4 text-center">
                        <p className="text-base text-gray-700">
                          <span className="font-semibold">Total Feedback Received:</span>{' '}
                          <span className="text-purple-600 font-bold text-lg">
                            {feedbackData.total_feedback_count || 0}
                          </span>
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {feedbackData.feedback_submitted_count || 0} of {feedbackData.total_staff_count || 0} staff submitted feedback
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center" style={{ minHeight: "320px" }}>
                      <div className="text-center">
                        <Star className="w-16 h-16 mx-auto text-gray-400 mb-3" />
                        <p className="text-sm text-gray-500 mb-1">No feedback data available</p>
                        <p className="text-xs text-gray-400">Feedback summary will appear once staff submit responses</p>
                      </div>
                    </div>
                  )}
                </Card>
                
                {/* Chapter-wise Feedback */}
                <Card className="p-6 bg-gradient-to-br from-blue-50 to-blue-100">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="font-semibold text-lg">Chapter-wise Feedback</h3>
                    <TooltipProvider>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <button className="text-gray-400 hover:text-gray-600">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs bg-gray-900 text-white">
                          <p>This section lists all chapters for which staff have submitted feedback, ordered by the number of feedback responses.</p>
                        </TooltipContent>
                      </UITooltip>
                    </TooltipProvider>
                  </div>
                  
                  {feedbackData.topic_feedback && feedbackData.topic_feedback.length > 0 ? (
                    <div className="space-y-3 max-h-[420px] overflow-y-auto">
                      {feedbackData.topic_feedback
                        .filter((topic) => (topic.response_count || 0) > 0)
                        .sort((a, b) => (b.response_count || 0) - (a.response_count || 0))
                        .map((topic, idx) => (
                          <div 
                            key={idx} 
                            className="bg-white rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow border border-blue-200"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                    idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                                    idx === 1 ? 'bg-gray-300 text-gray-900' :
                                    idx === 2 ? 'bg-orange-400 text-orange-900' :
                                    'bg-blue-100 text-blue-900'
                                  }`}>
                                    {idx + 1}
                                  </span>
                                  <p className="font-semibold text-gray-900 text-sm">{topic.topic_name}</p>
                                </div>
                                <div className="flex items-center gap-3 ml-10">
                                  <span className="text-lg font-bold text-purple-600">
                                    {topic.average_rating || 0}
                                  </span>
                                  <span className="text-sm text-gray-600">/ 5</span>
                                  <div className="ml-2">
                                    {renderStars(topic.average_rating || 0)}
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => handleTopicFeedbackClick(topic.topic_id, topic.topic_name)}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
                              >
                                {topic.response_count || 0} Response{topic.response_count !== 1 ? 's' : ''}
                              </button>
                            </div>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center" style={{ minHeight: "320px" }}>
                      <div className="text-center">
                        <MessageSquare className="w-16 h-16 mx-auto text-gray-400 mb-3" />
                        <p className="text-sm text-gray-500 mb-1">No chapter feedback available</p>
                        <p className="text-xs text-gray-400">Chapter ratings will appear once staff provide feedback</p>
                      </div>
                    </div>
                  )}
                </Card>
              </div>
            </Card>
          )}
          
          {/* J. Rewards & Recognition */}
          {activeTab === 'rewards' && tabLoading.rewards && <TabLoadingIndicator message="Loading Rewards & Recognition data..." />}
          {activeTab === 'rewards' && !tabLoading.rewards && (
            <Card className="p-6">
              <h2 className="text-[24px] font-bold mb-6" style={{ color: '#0056B3' }}>Rewards & Recognition</h2>
              
              <div className="grid grid-cols-3 gap-6 mb-6">
                {/* Top Performers */}
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg">Top Performers</h3>
                    <TooltipProvider>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <button className="text-gray-400 hover:text-gray-600">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs bg-gray-900 text-white">
                          <p>Top Performer is calculated using a composite performance score: 40% training completion, 40% assessment performance, and 20% reporting manager rating. The staff member with the highest composite score is shown here.</p>
                        </TooltipContent>
                      </UITooltip>
                    </TooltipProvider>
                  </div>
                  {overviewData.top_performers && overviewData.top_performers.length > 0 ? (
                    <>
                      <div className="space-y-2 mb-4">
                        {overviewData.top_performers.slice(0, 5).map((performer, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-200">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                                idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                                idx === 1 ? 'bg-gray-300 text-gray-900' :
                                idx === 2 ? 'bg-orange-400 text-orange-900' :
                                'bg-blue-100 text-blue-900'
                              }`}>
                                {idx + 1}
                              </div>
                              <div>
                                <p className="font-medium text-sm">{performer.name}</p>
                                <p className="text-xs text-gray-600">{performer.department}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-lg text-green-600">{performer.avg_score}%</p>
                            </div>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={handleAllPerformersClick}
                        className="w-full text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center justify-center gap-1 transition-colors"
                      >
                        View more performers
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <Trophy className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No top performers yet</p>
                    </div>
                  )}
                </Card>
                
                {/* Rising Star */}
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg">Rising Star</h3>
                    <TooltipProvider>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <button className="text-gray-400 hover:text-gray-600">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs bg-gray-900 text-white">
                          <p>Rising Star highlights the staff member showing the strongest improvement in composite performance over time, based on 40% training completion, 40% assessment performance, and 20% reporting manager rating.</p>
                        </TooltipContent>
                      </UITooltip>
                    </TooltipProvider>
                  </div>
                  {overviewData.top_performers && overviewData.top_performers.length > 2 ? (
                    <div className="p-4 bg-gradient-to-br from-green-50 to-white rounded-lg border border-green-200">
                      <div className="flex flex-col items-center mb-4">
                        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mb-3">
                          <Star className="w-12 h-12 text-green-600" />
                        </div>
                        <h4 className="font-bold text-lg text-gray-900">{overviewData.top_performers[2].name}</h4>
                        <p className="text-sm text-gray-600">{overviewData.top_performers[2].department}</p>
                      </div>
                      <div className="flex items-center justify-between pt-3 border-t border-green-200">
                        <span className="text-sm text-gray-600">Average Score</span>
                        <span className="font-bold text-lg text-green-600">{overviewData.top_performers[2].avg_score}%</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Star className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No rising star yet</p>
                    </div>
                  )}
                </Card>
                
                {/* Leading Departments */}
                <Card className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-lg">Leading Departments</h3>
                    <TooltipProvider>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <button className="text-gray-400 hover:text-gray-600">
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs bg-gray-900 text-white">
                          <p>Leading Departments are ranked based on the average composite performance of staff in each department. Composite performance is derived from 40% training completion, 40% assessment performance, and 20% reporting manager rating.</p>
                        </TooltipContent>
                      </UITooltip>
                    </TooltipProvider>
                  </div>
                  {overviewData.top_departments && overviewData.top_departments.length > 0 ? (
                    <>
                      <div className="space-y-2 mb-4">
                        {overviewData.top_departments.slice(0, 7).map((dept, idx) => (
                          <div key={idx} className="p-3 bg-gradient-to-r from-purple-50 to-white rounded-lg border border-purple-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Award className="w-4 h-4 text-purple-600" />
                                <p className="font-medium text-sm">{dept.name}</p>
                              </div>
                              <span className="font-bold text-purple-600">{dept.score}%</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={handleLeadingDepartmentsClick}
                        className="w-full text-blue-600 hover:text-blue-700 font-medium text-sm flex items-center justify-center gap-1 transition-colors"
                      >
                        See all departments
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <Award className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No department data yet</p>
                    </div>
                  )}
                </Card>
              </div>
            </Card>
          )}
          
          {/* K. Staff Activity Insights */}
          {activeTab === 'activity' && tabLoading.activity && <TabLoadingIndicator message="Loading Staff Activity data..." />}
          {activeTab === 'activity' && !tabLoading.activity && (
            <div className="space-y-6">
              <Card className="p-6">
                <h2 className="text-[24px] font-bold mb-6" style={{ color: '#0056B3' }}>Staff Activity Insights</h2>
                
                {/* Topic Completion Band Distribution Widget - MOVED TO TOP */}
                <Card className="p-6 mb-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold">Activity by Training Chapter – Completion Distribution</h3>
                    <TooltipProvider>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <button className="text-gray-400 hover:text-gray-600">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs bg-gray-900 text-white">
                          <p className="text-sm">Shows how training chapters are distributed across completion bands. Click a bar to see all chapters in that band.</p>
                        </TooltipContent>
                      </UITooltip>
                    </TooltipProvider>
                  </div>
                  
                  {topicDistributionLoading ? (
                    <div className="text-center py-12">
                      <div className="relative inline-block">
                        <div className="w-8 h-8 border-4 border-blue-200 rounded-full animate-spin border-t-blue-600"></div>
                      </div>
                      <p className="text-gray-500 mt-4">The data is loading…</p>
                    </div>
                  ) : topicDistributionData && topicDistributionData.total_topics > 0 ? (
                    <div className="border border-gray-200 rounded p-4 bg-white overflow-x-auto">
                      <div className="flex justify-center relative">
                        <BarChart 
                          width={600} 
                          height={320} 
                          data={topicDistributionData.distribution}
                          margin={{ top: 25, right: 20, left: 20, bottom: 5 }}
                          onMouseLeave={() => setHoveredBandIndex(null)}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis 
                            dataKey="band" 
                            tick={{ fontSize: 13, fontWeight: 600 }}
                            tickLine={false}
                            axisLine={{ stroke: '#E5E7EB' }}
                          />
                          <YAxis 
                            tick={{ fontSize: 12 }}
                            label={{ value: 'Chapters', angle: -90, position: 'insideLeft', fontSize: 12 }}
                            allowDecimals={false}
                            tickLine={false}
                            axisLine={{ stroke: '#E5E7EB' }}
                          />
                          <Bar 
                            dataKey="count" 
                            onClick={(data) => handleBandBarClick(data)}
                            cursor="pointer"
                            label={{ position: 'top', fontSize: 12, fontWeight: 'bold', fill: '#374151' }}
                            isAnimationActive={false}
                            barSize={80}
                          >
                            {topicDistributionData.distribution.map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={entry.color}
                                onMouseEnter={() => setHoveredBandIndex(index)}
                                onMouseLeave={() => setHoveredBandIndex(null)}
                                style={{ cursor: 'pointer' }}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                        
                        {/* Custom Tooltip - positioned based on hovered bar */}
                        {hoveredBandIndex !== null && topicDistributionData.distribution[hoveredBandIndex] && (
                          <div 
                            className="absolute bg-white p-4 border border-gray-200 rounded-lg shadow-xl max-w-sm pointer-events-none"
                            style={{ 
                              zIndex: 9999,
                              left: `${70 + (hoveredBandIndex * 140)}px`,
                              top: '50px'
                            }}
                          >
                            {(() => {
                              const bandData = topicDistributionData.distribution[hoveredBandIndex];
                              const topics = bandData.topics || [];
                              const displayTopics = topics.slice(0, 10);
                              const hasMore = topics.length > 10;
                              
                              return (
                                <>
                                  <div className="flex items-center gap-2 mb-3 pb-2 border-b">
                                    <div 
                                      className="w-3 h-3 rounded-full" 
                                      style={{ backgroundColor: bandData.color }}
                                    ></div>
                                    <p className="font-bold text-gray-900">{bandData.band} Completion</p>
                                  </div>
                                  <p className="text-sm text-gray-600 mb-2">
                                    <span className="font-semibold">{bandData.count}</span> chapter{bandData.count !== 1 ? 's' : ''} in this band
                                  </p>
                                  {topics.length > 0 && (
                                    <div className="max-h-48 overflow-y-auto">
                                      <p className="text-xs font-semibold text-gray-500 mb-1">Chapters:</p>
                                      {displayTopics.map((topic, idx) => (
                                        <div key={idx} className="flex justify-between items-center py-1 text-xs border-b border-gray-100 last:border-0">
                                          <span className="text-gray-700 truncate pr-2" style={{ maxWidth: '180px' }}>{topic.topic_name}</span>
                                          <span className="font-semibold" style={{ color: bandData.color }}>{topic.avg_completion}%</span>
                                        </div>
                                      ))}
                                      {hasMore && (
                                        <p className="text-xs text-blue-600 mt-2 font-medium">
                                          + {topics.length - 10} more chapters (click bar to view all)
                                        </p>
                                      )}
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                      
                      {/* Legend */}
                      <div className="flex justify-center gap-6 mt-4 pt-4 border-t">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-red-500"></div>
                          <span className="text-xs text-gray-600">0-40% (High Risk)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                          <span className="text-xs text-gray-600">41-60% (Needs Attention)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                          <span className="text-xs text-gray-600">61-80% (On Track)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                          <span className="text-xs text-gray-600">81-100% (Excellent)</span>
                        </div>
                      </div>
                      
                      <p className="text-center text-xs text-gray-400 mt-3">
                        Total: {topicDistributionData.total_topics} chapter{topicDistributionData.total_topics !== 1 ? 's' : ''} | Click on a bar to view all chapters in that band
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-gray-500">No chapters available for selected filters.</p>
                    </div>
                  )}
                </Card>
                
                {/* Two Widgets Side by Side - Completion Trend and Activity by Department */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                
                {/* Completion Trend Widget - Moved from Overview tab */}
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold">Completion Trend</h3>
                    <TooltipProvider>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <button className="text-gray-400 hover:text-gray-600">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs bg-gray-900 text-white">
                          <p className="text-sm">Shows the number of training chapters completed per day within the selected date range.</p>
                        </TooltipContent>
                      </UITooltip>
                    </TooltipProvider>
                  </div>

                  {/* Date Range Selector */}
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <div className="flex items-center gap-1">
                      <label className="text-xs font-medium text-gray-700">From:</label>
                      <input
                        type="date"
                        value={trendDateRange.startDate}
                        onChange={(e) => handleTrendDateChange(e.target.value, trendDateRange.endDate)}
                        className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <label className="text-xs font-medium text-gray-700">To:</label>
                      <input
                        type="date"
                        value={trendDateRange.endDate}
                        onChange={(e) => handleTrendDateChange(trendDateRange.startDate, e.target.value)}
                        className="border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <button
                      onClick={() => {
                        // Reset to hospital signup date (or fallback to 1 year ago)
                        const resetStartDate = hospitalSignupDate || (() => {
                          const oneYearAgo = new Date();
                          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
                          return oneYearAgo.toISOString().split('T')[0];
                        })();
                        const today = new Date().toISOString().split('T')[0];
                        handleTrendDateChange(resetStartDate, today);
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2 py-1 border border-blue-300 rounded hover:bg-blue-50"
                    >
                      Reset
                    </button>
                  </div>

                  {/* Line Chart */}
                  {completionTrendData && completionTrendData.length > 0 ? (
                    <div className="border border-gray-200 rounded p-2 bg-white">
                      <LineChart width={450} height={280} data={completionTrendData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 10 }}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis 
                          tick={{ fontSize: 10 }}
                          label={{ value: 'Chapters Completed', angle: -90, position: 'insideLeft', fontSize: 10 }}
                          allowDecimals={false}
                        />
                        <Tooltip 
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              return (
                                <div className="bg-white p-2 border border-gray-200 rounded shadow-lg">
                                  <p className="text-xs font-semibold">{payload[0].payload.date}</p>
                                  <p className="text-xs text-blue-600">
                                    Completed: {payload[0].value} chapter{payload[0].value !== 1 ? 's' : ''}
                                  </p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="completions_count" 
                          stroke="#3B82F6" 
                          strokeWidth={2}
                          dot={{ r: 3 }}
                          activeDot={{ r: 5 }}
                        />
                      </LineChart>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500 text-sm">No data available for the selected period.</p>
                    </div>
                  )}
                </Card>
                
                {/* Activity by Department Widget */}
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold">Activity by Department</h3>
                    <TooltipProvider>
                      <UITooltip>
                        <TooltipTrigger asChild>
                          <button className="text-gray-400 hover:text-gray-600">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs bg-gray-900 text-white">
                          <p className="text-sm">Department-wise average chapter completion, sorted alphabetically.</p>
                        </TooltipContent>
                      </UITooltip>
                    </TooltipProvider>
                  </div>

                  {activityDataLoading ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500 text-sm">The data is loading…</p>
                    </div>
                  ) : activityByDepartment && activityByDepartment.length > 0 ? (
                    <div className="border border-gray-200 rounded p-2 bg-white">
                      <BarChart width={450} height={280} data={activityByDepartment}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="department" 
                          tick={{ fontSize: 10 }}
                          angle={-45}
                          textAnchor="end"
                          height={60}
                        />
                        <YAxis 
                          domain={[0, 100]}
                          tick={{ fontSize: 10 }}
                          label={{ value: 'Completion %', angle: -90, position: 'insideLeft', fontSize: 10 }}
                        />
                        <Tooltip 
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              return (
                                <div className="bg-white p-2 border border-gray-200 rounded shadow-lg">
                                  <p className="text-xs font-semibold">{payload[0].payload.department}</p>
                                  <p className="text-xs text-green-600">
                                    Avg: {payload[0].value}%
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    Staff: {payload[0].payload.staff_count}
                                  </p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                        <Bar 
                          dataKey="avg_completion" 
                          fill="#4CAF50" 
                        />
                      </BarChart>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-gray-500 text-sm">No data available for the selected period.</p>
                    </div>
                  )}
                </Card>
                </div>
              
            </Card>
            </div>
          )}
        </div>
      
        {/* Topic Completion Band Distribution Modal */}
        {topicDistributionModalOpen && selectedBandData && (
          <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: selectedBandData.color }}
                  ></div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      Chapters with {selectedBandData.band} Completion
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {selectedBandData.count} chapter{selectedBandData.count !== 1 ? 's' : ''} in this completion band
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setTopicDistributionModalOpen(false);
                    setSelectedBandData(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Modal Body - Scrollable Topic List */}
              <div className="flex-1 overflow-y-auto p-6">
                {selectedBandData.topics && selectedBandData.topics.length > 0 ? (
                  <div className="space-y-2">
                    {/* Table Header */}
                    <div className="grid grid-cols-12 gap-4 px-4 py-2 bg-gray-100 rounded-lg font-semibold text-sm text-gray-700">
                      <div className="col-span-7">Chapter Name</div>
                      <div className="col-span-3 text-center">Avg Completion</div>
                      <div className="col-span-2 text-center">Staff</div>
                    </div>
                    
                    {/* Topic Rows */}
                    {selectedBandData.topics.map((topic, idx) => (
                      <div 
                        key={idx} 
                        className="grid grid-cols-12 gap-4 px-4 py-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="col-span-7 text-gray-900 font-medium truncate" data-tooltip={topic.topic_name}>
                          {topic.topic_name}
                        </div>
                        <div className="col-span-3 text-center">
                          <span 
                            className="inline-block px-3 py-1 rounded-full text-sm font-semibold text-white"
                            style={{ backgroundColor: selectedBandData.color }}
                          >
                            {topic.avg_completion}%
                          </span>
                        </div>
                        <div className="col-span-2 text-center text-gray-600">
                          {topic.staff_count}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No chapters in this band.</p>
                  </div>
                )}
              </div>
              
              {/* Modal Footer */}
              <div className="p-4 border-t border-gray-200 bg-gray-50">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-500">
                    Sorted by completion % (ascending)
                  </p>
                  <button
                    onClick={() => {
                      setTopicDistributionModalOpen(false);
                      setSelectedBandData(null);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Staff Performance Distribution Modal */}
        {staffPerfDistModalOpen && selectedPerfBandData && (
          <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: selectedPerfBandData.color }}
                  ></div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">
                      {selectedPerfBandData.band} - Staff List
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {selectedPerfBandData.count} staff member{selectedPerfBandData.count !== 1 ? 's' : ''} in this performance band
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setStaffPerfDistModalOpen(false);
                    setSelectedPerfBandData(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Modal Body - Scrollable Staff List */}
              <div className="flex-1 overflow-y-auto p-6">
                {selectedPerfBandData.staff && selectedPerfBandData.staff.length > 0 ? (
                  <div className="space-y-2">
                    {/* Table Header */}
                    <div className="grid grid-cols-12 gap-2 px-4 py-2 bg-gray-100 rounded-lg font-semibold text-xs text-gray-700">
                      <div className="col-span-3">Staff Name</div>
                      <div className="col-span-2">Department</div>
                      <div className="col-span-2 text-center">Composite</div>
                      <div className="col-span-2 text-center">Completion</div>
                      <div className="col-span-2 text-center">Assessment</div>
                      <div className="col-span-1 text-center">Rating</div>
                    </div>
                    
                    {/* Staff Rows */}
                    {selectedPerfBandData.staff.map((staff, idx) => (
                      <div 
                        key={idx} 
                        className="grid grid-cols-12 gap-2 px-4 py-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="col-span-3 text-gray-900 font-medium truncate" data-tooltip={staff.name}>
                          {staff.name}
                        </div>
                        <div className="col-span-2 text-gray-600 text-sm truncate" data-tooltip={staff.department}>
                          {staff.department}
                        </div>
                        <div className="col-span-2 text-center">
                          <span 
                            className="inline-block px-2 py-1 rounded-full text-xs font-semibold text-white"
                            style={{ backgroundColor: selectedPerfBandData.color }}
                          >
                            {staff.composite_score}%
                          </span>
                        </div>
                        <div className="col-span-2 text-center text-sm text-gray-700">
                          {staff.completion_pct}%
                        </div>
                        <div className="col-span-2 text-center text-sm text-gray-700">
                          {staff.assessment_pct}%
                        </div>
                        <div className="col-span-1 text-center text-sm text-gray-700">
                          {staff.manager_rating_pct}%
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No staff in this performance band.</p>
                  </div>
                )}
              </div>
              
              {/* Modal Footer */}
              <div className="p-4 border-t border-gray-200 bg-gray-50">
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-500">
                    Sorted by composite score (highest first) | Formula: (Completion × 40%) + (Assessment × 40%) + (Manager Rating × 20%)
                  </p>
                  <button
                    onClick={() => {
                      setStaffPerfDistModalOpen(false);
                      setSelectedPerfBandData(null);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      
        {/* Staff vs Topic Matrix Modal */}
        {matrixModalOpen && (
          <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] flex flex-col">
              {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Staff Activity Matrix</h2>
                <p className="text-sm text-gray-500 mt-1">Visual overview of staff training activities and completion progress</p>
              </div>
              <button
                onClick={() => setMatrixModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Filters Subchapter */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <select
                    value={matrixFilterDepartment}
                    onChange={(e) => setMatrixFilterDepartment(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Departments</option>
                    {matrixDepartments.map(dept => (
                      <option key={dept} value={dept}>{dept}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Role</label>
                  <select
                    value={matrixFilterJobRole}
                    onChange={(e) => setMatrixFilterJobRole(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Job Roles</option>
                    {matrixJobRoles.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Staff Name</label>
                  <input
                    type="text"
                    value={matrixFilterStaffName}
                    onChange={(e) => setMatrixFilterStaffName(e.target.value)}
                    placeholder="Search by name..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
            
            {/* Modal Body - Scrollable Matrix */}
            <div className="flex-1 overflow-auto p-6">
              {/* Loading State */}
              {matrixLoading ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-gray-600 font-medium">Loading Staff Activity Matrix...</p>
                  <p className="text-gray-400 text-sm mt-1">Please wait while we fetch the data</p>
                </div>
              ) : (() => {
                // Apply filters to staff data
                // API returns department_name and job_function_name fields
                const filteredStaff = matrixData.staff.filter(staff => {
                  const staffDept = staff.department_name || staff.department;
                  const staffRole = staff.job_function_name || staff.job_function || staff.job_role;
                  const matchesDepartment = matrixFilterDepartment === 'all' || staffDept === matrixFilterDepartment;
                  const matchesJobRole = matrixFilterJobRole === 'all' || staffRole === matrixFilterJobRole;
                  const matchesName = !matrixFilterStaffName || staff.full_name?.toLowerCase().includes(matrixFilterStaffName.toLowerCase());
                  return matchesDepartment && matchesJobRole && matchesName;
                });
                
                return filteredStaff.length > 0 && matrixData.topics.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead className="sticky top-0 bg-white z-10">
                      <tr>
                        <th className="sticky left-0 z-20 bg-white border-2 border-gray-300 p-3 text-left font-bold text-gray-900 min-w-[200px]">
                          Staff Name
                        </th>
                        {matrixData.topics.map((topic) => (
                          <th 
                            key={topic.content_id} 
                            className="border-2 border-gray-300 p-3 text-center font-semibold text-gray-900 min-w-[120px]"
                            style={{ writingMode: 'horizontal-tb' }}
                          >
                            <TooltipProvider>
                              <UITooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex flex-col items-center cursor-help">
                                    <span className="text-xs line-clamp-2">{topic.title}</span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs bg-gray-900 text-white">
                                  <p>{topic.title}</p>
                                </TooltipContent>
                              </UITooltip>
                            </TooltipProvider>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStaff.map((staffMember) => (
                        <tr key={staffMember.user_id} className="hover:bg-gray-50">
                          <td className="sticky left-0 z-10 bg-white border border-gray-300 p-3 font-medium text-gray-900">
                            <div>
                              <div>{staffMember.full_name}</div>
                              <div className="text-xs text-gray-500">{staffMember.department_name || staffMember.department}</div>
                            </div>
                          </td>
                          {matrixData.topics.map((topic) => {
                            const assignment = matrixData.assignments[staffMember.user_id]?.[topic.content_id];
                            const isAssigned = !!assignment;
                            const completion = assignment?.completion || 0;
                            const status = assignment?.status || 'not_started';
                            
                            // Determine cell color based on completion
                            let bgColor = 'bg-gray-100'; // Not assigned
                            let textColor = 'text-gray-400';
                            
                            if (isAssigned) {
                              if (status === 'completed' || completion === 100) {
                                bgColor = 'bg-green-100';
                                textColor = 'text-green-800';
                              } else if (completion > 0) {
                                bgColor = 'bg-blue-100';
                                textColor = 'text-blue-800';
                              } else {
                                bgColor = 'bg-yellow-50';
                                textColor = 'text-yellow-800';
                              }
                            }
                            
                            return (
                              <td 
                                key={topic.content_id}
                                className={`border border-gray-300 p-3 text-center ${bgColor}`}
                              >
                                {isAssigned ? (
                                  <div className="flex flex-col items-center gap-1">
                                    <span className={`text-sm font-bold ${textColor}`}>
                                      {Math.round(completion)}%
                                    </span>
                                    {status === 'completed' && (
                                      <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                      </svg>
                                    )}
                                  </div>
                                ) : (
                                  <span className={`text-xs ${textColor}`}>-</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-gray-600 font-medium">
                    {matrixData.staff.length === 0 ? 'No staff data available' : 
                     matrixData.topics.length === 0 ? 'No training chapters available' :
                     'No staff match the current filters'}
                  </p>
                  <p className="text-gray-500 text-sm mt-1">
                    {matrixData.staff.length === 0 || matrixData.topics.length === 0 
                      ? 'Staff and chapter data will appear once they are added'
                      : 'Try adjusting the filters above'}
                  </p>
                </div>
              );
              })()}
            </div>
            
            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-100 border border-green-300 rounded"></div>
                  <span className="text-gray-700">Completed (100%)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-blue-100 border border-blue-300 rounded"></div>
                  <span className="text-gray-700">In Progress</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-yellow-50 border border-yellow-300 rounded"></div>
                  <span className="text-gray-700">Not Started</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-gray-100 border border-gray-300 rounded"></div>
                  <span className="text-gray-700">Not Assigned</span>
                </div>
              </div>
              <button
                onClick={() => setMatrixModalOpen(false)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Staff Details Modal */}
      {staffDetailsModalOpen && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {staffDetailsFilter === 'all' && 'All Staff Details'}
                  {staffDetailsFilter === 'active' && 'Active Staff Details'}
                  {staffDetailsFilter === 'inactive' && 'Inactive Staff Details'}
                  {staffDetailsFilter === 'archived' && 'Archived Staff Details'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Staff information with training completion progress
                </p>
              </div>
              <button
                onClick={() => setStaffDetailsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Body - Scrollable Table */}
            <div className="flex-1 overflow-auto p-6">
              {staffDetailsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <p className="mt-4 text-gray-600">Loading staff details...</p>
                  </div>
                </div>
              ) : staffDetailsData.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead className="sticky top-0 bg-gray-50 z-10">
                      <tr>
                        <th className="border border-gray-300 p-3 text-left font-semibold text-gray-900">
                          Staff Name
                        </th>
                        <th className="border border-gray-300 p-3 text-left font-semibold text-gray-900">
                          Email
                        </th>
                        <th className="border border-gray-300 p-3 text-left font-semibold text-gray-900">
                          Department
                        </th>
                        <th className="border border-gray-300 p-3 text-left font-semibold text-gray-900">
                          Registration Date
                        </th>
                        <th className="border border-gray-300 p-3 text-center font-semibold text-gray-900">
                          Overall Completion
                        </th>
                        <th className="border border-gray-300 p-3 text-center font-semibold text-gray-900">
                          Chapters Progress
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {staffDetailsData.map((staff, index) => (
                        <tr key={staff.user_id || index} className="hover:bg-gray-50">
                          <td className="border border-gray-300 p-3 text-gray-900">
                            {staff.name}
                          </td>
                          <td className="border border-gray-300 p-3 text-gray-700">
                            {staff.email}
                          </td>
                          <td className="border border-gray-300 p-3 text-gray-700">
                            {staff.department}
                          </td>
                          <td className="border border-gray-300 p-3 text-gray-700">
                            {staff.registration_date}
                          </td>
                          <td className="border border-gray-300 p-3 text-center">
                            <div className="flex flex-col items-center gap-1">
                              <div className="w-full bg-gray-200 rounded-full h-2 max-w-[100px]">
                                <div 
                                  className="bg-blue-600 h-2 rounded-full transition-all"
                                  style={{ width: `${staff.completion_percentage}%` }}
                                ></div>
                              </div>
                              <span className={`text-sm font-bold ${
                                staff.completion_percentage === 100 ? 'text-green-600' :
                                staff.completion_percentage > 0 ? 'text-blue-600' :
                                'text-gray-500'
                              }`}>
                                {staff.completion_percentage}%
                              </span>
                            </div>
                          </td>
                          <td className="border border-gray-300 p-3 text-center text-gray-700">
                            <span className="text-sm">
                              {staff.completed_topics}/{staff.total_topics}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <p className="text-gray-600 font-medium">No staff found</p>
                  <p className="text-gray-500 text-sm mt-1">
                    {staffDetailsFilter === 'active' && 'No staff with training activity found'}
                    {staffDetailsFilter === 'inactive' && 'No staff without training activity found'}
                    {staffDetailsFilter === 'archived' && 'No archived staff found'}
                    {staffDetailsFilter === 'all' && 'No staff members found'}
                  </p>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                {!staffDetailsLoading && (
                  <span>Showing {staffDetailsData.length} staff member{staffDetailsData.length !== 1 ? 's' : ''}</span>
                )}
              </div>
              <button
                onClick={() => setStaffDetailsModalOpen(false)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Department List Modal */}
      {departmentListModalOpen && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">All Departments</h2>
                <p className="text-sm text-gray-500 mt-1">Staff average assessment scores by department</p>
              </div>
              <button
                onClick={() => setDepartmentListModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Body - Scrollable Table */}
            <div className="flex-1 overflow-auto p-6">
              {departmentListLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <p className="mt-4 text-gray-600">Loading departments...</p>
                  </div>
                </div>
              ) : departmentListData.length > 0 ? (
                <div className="space-y-4">
                  {departmentListData.map((dept, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-base font-semibold text-gray-900">{dept.department_name}</h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {dept.staff_count} staff member{dept.staff_count !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className={`text-2xl font-bold ${
                            dept.average_score >= 75 ? 'text-green-600' :
                            dept.average_score >= 50 ? 'text-blue-600' :
                            dept.average_score >= 25 ? 'text-orange-600' :
                            'text-red-600'
                          }`}>
                            {dept.average_score}%
                          </span>
                          <p className="text-xs text-gray-500 mt-1">Avg Score</p>
                        </div>
                      </div>
                      
                      <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500 ${
                            dept.average_score >= 75 ? 'bg-green-500' :
                            dept.average_score >= 50 ? 'bg-blue-500' :
                            dept.average_score >= 25 ? 'bg-orange-500' :
                            'bg-red-500'
                          }`}
                          style={{ width: `${Math.min(dept.average_score, 100)}%` }}
                        />
                      </div>
                      
                      {dept.total_assessments > 0 && (
                        <p className="text-xs text-gray-500 mt-2">
                          {dept.total_assessments} assessment{dept.total_assessments !== 1 ? 's' : ''} taken
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <p className="text-gray-600 font-medium">No departments found</p>
                  <p className="text-gray-500 text-sm mt-1">Departments will appear once they are created</p>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                {!departmentListLoading && (
                  <span>Showing {departmentListData.length} department{departmentListData.length !== 1 ? 's' : ''} with ongoing training activity</span>
                )}
              </div>
              <button
                onClick={() => setDepartmentListModalOpen(false)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* All Performers Modal */}
      {allPerformersModalOpen && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">All Performers</h2>
                <p className="text-sm text-gray-500 mt-1">All staff ranked by average assessment score</p>
              </div>
              <button
                onClick={() => setAllPerformersModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex-1 overflow-auto p-6">
              {allPerformersLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <p className="mt-4 text-gray-600">Loading performers...</p>
                  </div>
                </div>
              ) : allPerformersData.length > 0 ? (
                <div className="space-y-3">
                  {allPerformersData.map((performer, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                          index === 0 ? 'bg-yellow-400 text-yellow-900' :
                          index === 1 ? 'bg-gray-300 text-gray-900' :
                          index === 2 ? 'bg-orange-400 text-orange-900' :
                          'bg-blue-100 text-blue-900'
                        }`}>
                          {index + 1}
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{performer.name}</h4>
                          <p className="text-sm text-gray-500">{performer.department}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-green-600">{performer.avg_score}%</p>
                        <p className="text-xs text-gray-500">{performer.completed_topics}/{performer.total_topics} chapters</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">No performers found</p>
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                {!allPerformersLoading && (
                  <span>Showing {allPerformersData.length} performer{allPerformersData.length !== 1 ? 's' : ''}</span>
                )}
              </div>
              <button
                onClick={() => setAllPerformersModalOpen(false)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leading Departments Modal */}
      {leadingDepartmentsModalOpen && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">All Departments</h2>
                <p className="text-sm text-gray-500 mt-1">Departments ranked by average staff assessment scores</p>
              </div>
              <button
                onClick={() => setLeadingDepartmentsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex-1 overflow-auto p-6">
              {leadingDepartmentsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <p className="mt-4 text-gray-600">Loading departments...</p>
                  </div>
                </div>
              ) : leadingDepartmentsData.length > 0 ? (
                <div className="space-y-3">
                  {leadingDepartmentsData.map((dept, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <Award className="w-8 h-8 text-purple-600" />
                          <div>
                            <h4 className="font-semibold text-gray-900">{dept.department_name}</h4>
                            <p className="text-sm text-gray-500">{dept.staff_count} staff members</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-purple-600">{dept.avg_score}%</p>
                          <p className="text-xs text-gray-500">Avg Score</p>
                        </div>
                      </div>
                      <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="absolute top-0 left-0 h-full bg-purple-500 rounded-full transition-all"
                          style={{ width: `${Math.min(dept.avg_score, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Award className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">No departments found</p>
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                {!leadingDepartmentsLoading && (
                  <span>Showing {leadingDepartmentsData.length} department{leadingDepartmentsData.length !== 1 ? 's' : ''} with ongoing training activity</span>
                )}
              </div>
              <button
                onClick={() => setLeadingDepartmentsModalOpen(false)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Topic Feedback Details Modal */}
      {topicFeedbackModalOpen && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Feedback for: {topicFeedbackData.topic_name}</h2>
                <p className="text-sm text-gray-500 mt-1">Detailed staff feedback for this training chapter</p>
              </div>
              <button
                onClick={() => setTopicFeedbackModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex-1 overflow-auto p-6">
              {topicFeedbackLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    <p className="mt-4 text-gray-600">Loading feedback...</p>
                  </div>
                </div>
              ) : topicFeedbackData.feedback_list && topicFeedbackData.feedback_list.length > 0 ? (
                <div className="space-y-4">
                  {topicFeedbackData.feedback_list.map((feedback, index) => (
                    <div key={index} className="p-5 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h4 className="font-semibold text-gray-900 text-lg">{feedback.staff_name}</h4>
                          <p className="text-sm text-gray-500">{feedback.staff_email}</p>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-2xl font-bold text-purple-600">{feedback.rating || 'N/A'}</span>
                            <span className="text-sm text-gray-600">/ 5</span>
                          </div>
                          <div className="flex items-center justify-end">
                            {feedback.rating && renderStars(feedback.rating)}
                          </div>
                        </div>
                      </div>
                      
                      {feedback.submitted_at && (
                        <p className="text-xs text-gray-400 mb-2">
                          Submitted: {new Date(feedback.submitted_at).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      )}
                      
                      {feedback.comment && (
                        <div className="mt-3 pt-3 border-t border-gray-300">
                          <p className="text-sm font-medium text-gray-700 mb-1">Comment:</p>
                          <p className="text-sm text-gray-600 italic">"{feedback.comment}"</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">No feedback found</p>
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <div className="text-sm text-gray-600">
                {!topicFeedbackLoading && (
                  <span>Total: {topicFeedbackData.feedback_list?.length || 0} feedback response{topicFeedbackData.feedback_list?.length !== 1 ? 's' : ''}</span>
                )}
              </div>
              <button
                onClick={() => setTopicFeedbackModalOpen(false)}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Compliance Modal */}
      {complianceModalOpen && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {complianceModalType === 'fully' && 'Fully Compliant Departments'}
                  {complianceModalType === 'partially' && 'Partially Compliant Departments'}
                  {complianceModalType === 'non' && 'Non-Compliant Departments'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {complianceModalType === 'fully' && '100% training completion'}
                  {complianceModalType === 'partially' && 'Between 1% - 99% training completion'}
                  {complianceModalType === 'non' && '0% training completion'}
                </p>
              </div>
              <button
                onClick={() => setComplianceModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex-1 overflow-auto p-6">
              {complianceModalData && complianceModalData.length > 0 ? (
                <div className="space-y-3">
                  {complianceModalData.map((dept, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{dept.department_name}</h3>
                        {complianceModalType === 'partially' && (
                          <div className="mt-2 flex items-center gap-3">
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-orange-500 h-2 rounded-full transition-all" 
                                style={{ width: `${dept.completion_percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-sm font-medium text-gray-700">
                              {dept.completion_percentage}%
                            </span>
                          </div>
                        )}
                        <div className="mt-1 text-sm text-gray-500">
                          {dept.completed_count} of {dept.required_count} required trainings completed
                        </div>
                      </div>
                      <div className={`ml-4 px-3 py-1 rounded-full text-sm font-medium ${
                        complianceModalType === 'fully' ? 'bg-green-100 text-green-800' :
                        complianceModalType === 'partially' ? 'bg-orange-100 text-orange-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {dept.completion_percentage}%
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">No departments in this category</p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setComplianceModalOpen(false)}
                className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Risky Departments Modal */}
      {riskyDepartmentsModalOpen && (
        <div className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">All Compliance Risky Departments</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Departments with 0% completion or less than 60% completion
                </p>
              </div>
              <button
                onClick={() => setRiskyDepartmentsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="flex-1 overflow-auto p-6">
              {auditData && auditData.risky_departments && auditData.risky_departments.length > 0 ? (
                <div className="space-y-3">
                  {auditData.risky_departments.map((dept, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-4 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border-2 border-red-200"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        {/* Alert Icon */}
                        <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{dept.department_name}</h3>
                          {dept.completion_percentage === 0 ? (
                            <span className="inline-block mt-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-full">
                              Not Started
                            </span>
                          ) : (
                            <div className="mt-2 flex items-center gap-3">
                              <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-xs">
                                <div 
                                  className="bg-red-500 h-2 rounded-full transition-all" 
                                  style={{ width: `${dept.completion_percentage}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-gray-500">
                                {dept.completed_count} of {dept.required_count} trainings
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="ml-4 px-4 py-2 rounded-full text-lg font-bold bg-red-100 text-red-700">
                        {dept.completion_percentage}%
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">No risky departments found</p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => setRiskyDepartmentsModalOpen(false)}
                className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      </ResponsiveContainer>
    </AdminLayout>
  );
};

export default ReportsDashboardPage;
